import { createContext, useContext, useState, useEffect, ReactNode } from "react";

const LANGUAGE_STORAGE_KEY = "lce.language";

// Language context for bilingual support (EN/FR)
type Language = "en" | "fr";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    nav: {
      home: "Home",
      resources: "The Framework",
      threePillars: "The Three Pillars",
      dashboard: "The 3 Pillars",
      profile: "Profile",
      signOut: "Sign Out",
      signIn: "Sign In",
    },
    navDropdown: {
      induction: "Induction",
      confidence: "Confidence Builder",
      phonetics: "Phonetics Playground",
      vocabulary: "Vocabulary Gym",
      fluency: "Fluency, Step by Step",
      learnSmarter: "Learn Smarter",
      coaching: "Coaching",
      mysteryCalls: "Mystery Calls",
    },
    home: {
      title: "Le Coin Étude",
      subtitle: "What would you like to learn today?",
      intro: "Welcome to your personalised French learning hub! Here, you'll find everything you need to succeed on your French journey. Access a rich library of learning resources you can explore at your own pace, along with all the teaching materials we use in our sessions together. Book and join 1-1 coaching calls with me, access your Mystery Call meet links with instructions and assessment forms—all in one convenient place. Review your monthly Real-World Missions, upload your completed materials, and receive personalised feedback in a dedicated space. Plus, stay updated with the latest announcements and website updates right here on your home page. Let's make your French learning journey exciting and effective!",
      startTraining: "Start Training",
      categoriesTitle: "Learning Categories",
      categoriesSubtitle: "Choose a category to access your learning materials",
      ctaTitle: "Ready to Continue Learning?",
      ctaDescription: "All your resources are waiting for you. Start exploring and take your French to the next level.",
      viewAllResources: "View All Resources",
    },
    category: {
      induction: {
        title: "Induction",
        desc: "Get started with your French learning journey",
      },
      podcast: {
        title: "Quirky French Podcast",
        desc: "Build confidence, sharpen your focus and take charge of your French learning journey",
      },
      topics: {
        title: "The Vocabulary Gym",
        desc: "Workout your French vocabulary with themed exercises and challenges",
      },
      radio: {
        title: "Quirky French Radio",
        desc: "Radio shows and audio content",
      },
      phonetics: {
        title: "Phonetics Playground",
        desc: "Master French pronunciation and sounds through playful practice",
      },
      grammar: {
        title: "Learn Smarter, Not Harder",
        desc: "Discover how to learn effectively and acquire French with more ease and less tension",
      },
      confidence: {
        title: "Confidence",
        desc: "Build confidence in your French skills",
      },
    },
    pillars: {
      title: "The Three Pillars",
      subtitle: "Three unique ways to accelerate your French learning journey through personalised coaching, real-world practise, and exciting challenges.",
      coaching: {
        title: "1-1 Coaching",
        desc: "Personalised guidance to help you overcome challenges and reach your goals",
        text: "Work directly with me in one-on-one sessions to develop your French skills, overcome mental blocks, and create a personalised learning strategy.",
        bullet1: "1h30 live coaching with me 1-1 to develop your skills overall and work towards your goal",
        bullet2: "Tailored feedback and action plans",
        bullet3: "Overcome your fear and other mental blocks",
        bullet4: "Get outside of your comfort zone and grow!",
        button: "Learn More About Coaching",
        joinMeeting: "Join Coaching Session",
        noLinkAvailable: "Your coaching session link will appear here once it's been set up.",
      },
      mystery: {
        title: "The Mystery Calls",
        desc: "Surprise phone conversations to practise your French in real-time",
        text: "Receive unexpected calls where you'll engage in authentic French conversations. No preparation, just real practise!",
        bullet1: "Spontaneous speaking practise",
        bullet2: "Build confidence in real conversations",
        bullet3: "Overcome fear of speaking",
        bullet4: "Get immediate feedback",
        button: "Learn More About Mystery Calls",
        joinCall: "Join Mystery Call",
        noLinkAvailable: "Your Mystery Call link will appear here once it's been set up.",
      },
      missions: {
        title: "Real-World Missions",
        desc: "Practical challenges to apply your French in everyday situations",
        text: "Complete exciting missions that push you to use your French in authentic, real-life scenarios and build practical communication skills.",
        bullet1: "Custom-made \"Core Confidence Mission\" to challenge yourself in real-life",
        bullet2: "Video instructions and personalised handbook",
        bullet3: "Dedicated feedback session after you completed the task",
        bullet4: "Prove to yourself how much you're capable of!",
        button: "Learn More About Missions",
      },
      cta: {
        title: "Ready to Take Your French to the Next Level?",
        description: "These three pillars work together to provide comprehensive support for your French learning journey. Choose the path that resonates with you, or combine them for maximum impact!",
        button: "Explore All Resources",
      },
    },
    resources: {
      title: "Learning Resources",
      subtitle: "Access all your French learning materials organised by category",
      loading: "Loading resources...",
      noResources: "No resources available in this category yet.",
      backToCategories: "Back to Categories",
      podcast: {
        title: "Listen to Quirky French Podcast",
      },
      phonetics: {
        title: "Phonetics Courses",
        description: "Welcome to La Cour de Récré Phonétique! Here you'll find ear-training activities and pronunciation exercises designed to help you master French sounds.",
        button: "Submit Your Recordings",
        introTitle: "Introduction to Phonetics",
        accentChallenge: "Accent Challenge",
        accentChallengeDesc: "Master French pronunciation with guided exercises and personalized feedback",
        phoneticsCorrection: "Phonetics Correction",
        phoneticsCorrectionDesc: "Get detailed corrections on your pronunciation",
        frenchFlow: "French Flow",
        frenchFlowDesc: "Develop natural rhythm and fluency in French",
        syllableWonderland: "Syllable Wonderland",
        syllableWonderlandDesc: "Practice French syllable patterns and structure",
        voiceoverBooth: "The Voiceover Booth",
        voiceoverBoothDesc: "Practice professional French speaking and expression",
        comingSoon: "Coming Soon",
      },
      grammar: {
        title: "Verb Echo",
        description: "Master French verb conjugations with our interactive trainer. Practise different tenses and moods in both structured and random modes to build your confidence.",
        button: "Start Practising",
      },
      vocabularyGym: "Vocabulary Gym",
      vocabularyGymDesc: "Expand your word bank, make vocabulary stick and unlock your passive knowledge",
      pathToFluency: "Fluency, Step by Step",
      pathToFluencyDesc: "Learn to lift the barriers between your thoughts and your speech — from emotions to pronunciation — and communicate with more ease",
      comingSoon: "Coming Soon",
      videoPlaceholder: "Video Placeholder",
      grammarCourses: "Grammar Courses",
      grammarIntro: "Master French grammar with our comprehensive courses designed to build your confidence and accuracy.",
      conjugationTrainer: "Verb Echo",
      conjugationTrainerDesc: "Train French verb conjugations through listening and speaking",
      grammarFundamentals: "Grammar Fundamentals",
      grammarFundamentalsDesc: "Essential grammar rules and structures",
      advancedGrammar: "Advanced Grammar",
      advancedGrammarDesc: "Complex structures and nuanced usage",
      fluencyTools: "Fluency Tools",
      fluencyIntro: "Fluency is about reducing friction between your thoughts and your speech. In this course, you won't learn more French — instead, you'll develop the essential soft skills to lift the barriers that slow you down: your emotions, your thoughts, your vocabulary, your grammar, and your pronunciation. Work through the modules at your own pace, focus on one barrier at a time, and watch your communication transform.",
      aiConversationPractice: "AI Conversation Practice",
      aiConversationDesc: "Practice real conversations with AI using voice",
      tryNow: "Try Now",
      fluencyChallenges: "Fluency Challenges",
      fluencyChallengesDesc: "Daily challenges to improve your fluency",
      expressionBuilder: "Expression Builder",
      expressionBuilderDesc: "Learn and practice natural French expressions",
      listening: {
        title: "UHD Listening Skills",
        description: "Train your ear to truly hear French — sharpen your listening instincts and build the soft skills that keep you in the flow of real conversations.",
      },
      induction: {
        welcome: "Welcome to Le Coin Étude.",
        skillOfLearning: "Welcome to Le Coin Étude",
        skillOfLearningDesc: "How do we learn what we learn?",
        fourKeyConcepts: "Four key concepts",
        fourKeyConceptsDesc: "Discover four key concepts that will supercharge your learning.",
        mythAboutTime: "The myth about time",
        mythAboutTimeDesc: "Find your sweet spot and ideal practice routine.",
        engagement: "Engagement",
        engagementDesc: "It's not about learning fast or learning a lot. It's about committing to progress.",
        // Module 2 checklist
        m2WatchVideo: "Watch the video on the skill of learning",
        m2IdentifyStyle: "Identify your personal learning style",
        m2ReflectLearning: "Reflect on how you've learned languages or skills before",
        // Module 2 questions
        m2DefineLearning: "In your own words, define what learning means to you.",
        m2PastSkill: "Think about a skill you learned in the past, what did it take to move you from being a beginner to being skilled? What did you have to do to gain competence?",
        m2Strategies: "Here are some examples of learning strategies to learn French: learning new words by looking them up in a dictionary, watching movies in French, learning French songs, using flashcards... List the strategies you have been using in your learning. Write down as many as possible.",
        m2StrategiesImportance: "How important is it to develop effective learning strategies?",
        // Module 3 checklist
        m3WatchVideo: "Watch the video on four key concepts",
        m3UnderstandConcepts: "Take notes on each of the four key concepts",
        m3ApplyConcepts: "Think about how to apply these concepts to your French learning",
        // Module 3 questions
        m3GoalSetting: "Goal setting - Describe your reason for learning French.",
        m3UltimateGoal: "What is your ultimate goal? Be as clear as possible. For example \"I want to speak French\" is too vague. Although \"I want to speak French fluently\" is better, it is still way too unclear as fluency itself is a very complex concept and can mean many things. However \"Within a year I want to reach such level that I can travel to France on holiday and feel confident about speaking without having to ever switch to English\" is a much clearer goal.",
        m3ConstraintList: "Constraint - Brainstorm a list of the most important things you wish to improve on in French.",
        m3ConstraintPriority: "Now look at each listed element. If you could improve on one item only, which one would you pick? Would that bring you closer to your ultimate goal? Yes: move on to question 5. No: have a look at the list and choose the most important element that would bring you closer to your goal before moving to question 5.",
        m3TopPriorities: "Now repeat the process by looking at the leftover listed elements and pick the next most important thing that you work on if you could only work on one area. Continue the process in question 4 until you can list your top 5 priorities. Write them down here:\n1.\n2.\n3.\n4.\n5.",
        m3Courage: "Courage - How do you expect to deal with setbacks and difficulties studying, focusing, keeping motivated etc? Brainstorm some ideas to prepare for the \"bad days\" to help you stay committed and engaged in the learning process.",
        m3Consistency: "Consistency - Consistency is very important in the language learning process. The key is to learn little and often but the intensity of your work is not measured by the number of hours stacked into your learning. Have you been consistent in your learning so far? What inconsistent behaviours should you eliminate from your routine and what consistent behaviours can you implement from now on? Brainstorm some ideas here.",
        // Module 4 checklist
        m4WatchVideo: "Watch the video about the myth of time",
        m4CalculateTime: "Calculate your available practice time per week",
        m4CreateSchedule: "Create a realistic French practice schedule",
        // Module 4 questions
        m4StudyApproach: "What has been your approach to studying so far? Do you have a particular time routine?",
        m4TaskOriented: "From time-orientated to task-orientated. Describe what studying would now look like if you had a task-orientated approach to your learning?",
        m4ComplexTasks: "How would you approach large and complex study tasks that may take several hours to complete?",
        // Module 5 checklist
        m5WatchVideo: "Watch the video on engagement and commitment",
        m5Commitment: "Make a personal commitment to your French learning journey",
        m5ShareGoal: "Write down your accountability plan",
        // Module 5 questions
        m5Takeaway: "What do you take away from the inductions videos so far?",
        m5Prepared: "Do you feel prepared to take charge of your learning?",
        m5Observations: "What observations and questions have you got for me so far? Write them down here and get in touch with me to share and discuss any point if you wish to.",
      },
      blueprint: "The Blueprint",
      blueprintDesc: "Master French syntax and sentence structure",
      verbEcho: "Verb Echo",
      verbEchoDesc: "Boost your verbs through listening and speaking",
      fluency: {
        courseIntro: "Course Introduction",
        courseIntroDesc: "What fluency really means and how this course works",
        emotions: "1. Your emotions are slowing you down",
        emotionsDesc: "Overcoming the fear of speaking",
        thoughts: "2. Your thoughts are slowing you down",
        thoughtsDesc: "The 3-bullet points activity",
        vocabulary: "3. Your vocabulary is slowing you down",
        vocabularyDesc: "Developing circumlocution skills",
        grammar: "4. Your grammar is slowing you down",
        grammarDesc: "Building grammar fluency",
        body: "5. Your body is slowing you down",
        bodyDesc: "Pronunciation and physical fluency",
        aiPractice: "AI Conversation Practice",
        aiPracticeDesc: "Practice real conversations with an AI tutor",
      },
      confidence: {
        coursesTitle: "Confidence Courses",
        diary: "Quirky French Confidence Diary",
        diaryDesc: "A 4-week guided diary to crush speaking fears, build resilience, and transform your relationship with French — one quirky step at a time.",
        stayingConnected: "Staying Connected to French",
        stayingConnectedDesc: "A reflective listening companion to help you pause, reconnect, and stay close to French — without pressure, at your own pace.",
        playlist: "Confidence Playlist",
        playlistDesc: "Curated collection of confidence-boosting Quirky French Podcast episodes to empower your French journey.",
      },
      vocabularyCourses: "Vocabulary Courses",
      vocabularyIntro: "Expand your French vocabulary with themed courses and practical exercises designed to help you speak more naturally.",
      startHere: "Start here: how good are you at learning vocabulary?",
      startHereDesc: "Assess your vocabulary learning skills and discover your strengths",
      topicsOfMonth: "Topics of the Month",
      topicsOfMonthDesc: "Monthly themed vocabulary lessons and exercises",
      vocabularyPlaylist: "Vocabulary Playlist",
      vocabularyPlaylistDesc: "Curated collection of vocabulary resources and materials",
      passiveToActive: "From Passive to Active Vocabulary",
      passiveToActiveDesc: "Transform words you understand into words you use",
      backToVocabCourses: "Back to Vocabulary Courses",
      topicsOfMonthDetail: "Monthly themed vocabulary lessons and exercises to expand your French vocabulary",
      topicsCheckLevel: "Check your level",
      topicsOpenMaterials: "Open the materials",
      pronunciationRecorder: "My Voice Lab",
      pronunciationRecorderDesc: "Your personal space to record, experiment, and get detailed feedback on anything you want to say in French",
    },
    resourceCard: {
      accessResource: "Access Resource",
      copyLink: "Copy link",
      linkCopied: "Link copied",
      linkCopiedDesc: "Paste it into your browser if Google Drive is blocked.",
      couldNotCopy: "Could not copy",
      couldNotCopyDesc: "Please copy the link manually.",
      driveNote: "If Google Drive is blocked on your network, copy the link and open it in another browser/device.",
      audio: "Audio",
      video: "Video",
      pdf: "PDF",
      document: "Document",
    },
    auth: {
      welcome: "Welcome to Le Coin Étude",
      subtitle: "Sign in or create an account to start learning",
      signIn: "Sign In",
      signUp: "Sign Up",
      email: "Email",
      password: "Password",
      fullName: "Full Name",
      firstName: "First Name",
      lastName: "Last Name",
      signingIn: "Signing in...",
      creatingAccount: "Creating account...",
      createAccount: "Create Account",
      forgotPassword: "Forgot your password?",
      resetPassword: "Reset Password",
      resetPasswordDesc: "Enter your email and we'll send you a link to reset your password.",
      sendResetLink: "Send Reset Link",
      sending: "Sending...",
      resetLinkSent: "Reset link sent! Check your email.",
      newPassword: "New Password",
      confirmPassword: "Confirm Password",
      updatePassword: "Update Password",
      updating: "Updating...",
      passwordUpdated: "Password updated successfully!",
      passwordMismatch: "Passwords do not match.",
      backToSignIn: "Back to Sign In",
    },
    profile: {
      loading: "Loading...",
      editProfile: "Edit Profile",
      cancel: "Cancel",
      firstName: "First Name",
      surname: "Surname",
      phoneNumber: "Phone Number",
      dateOfBirth: "Date of Birth",
      saveChanges: "Save Changes",
      currentStreak: "Current Streak",
      days: "days",
      keepGoing: "Keep it going!",
      completedSessions: "Completed Sessions",
      of: "of",
      total: "total",
      lastSession: "Last Session",
      noSessions: "No sessions",
      mostRecent: "Most recent activity",
      progress: "Progress",
      overallCompletion: "Overall completion",
      sessionCalendar: "Session Calendar",
      trackSessions: "Track your completed sessions",
      overallProgress: "Overall Progress",
      sessionsCompleted: "sessions completed",
      completed: "Completed",
      remaining: "Remaining",
      achievements: "Achievements",
      learningMilestones: "Your learning milestones",
      weekWarrior: "Week Warrior",
      weekStreak: "7 day streak achieved",
      sessionMaster: "Session Master",
      tenSessions: "Completed 10 sessions",
      halfwayHero: "Halfway Hero",
      fiftyPercent: "50% progress reached",
      avatarUpdated: "Avatar updated!",
      avatarFailed: "Failed to upload avatar",
      profileUpdated: "Profile updated!",
      profileFailed: "Failed to update profile",
      learner: "Learner",
      born: "Born",
      longestStreak: "Longest Streak",
      personalBest: "Personal best",
      resourcesFinished: "Resources finished",
      overallProgressDesc: "You've completed {completed} out of {total} resources",
      completedResources: "completed",
      inProgress: "in progress",
    },
    coaching: {
      title: "1-1 Coaching",
      description: "Your personal coaching hub - sessions, materials, and resources",
      joinCall: "Join Coaching Call",
      joinCallDesc: "Join your next 1-1 coaching session",
      myWorkspace: "My Workspace",
      myWorkspaceDesc: "View your workspace document",
      viewRecordings: "View Recordings",
      viewRecordingsDesc: "Browse your session recordings",
      sessionDocuments: "Session Documents",
      sessionDocumentsDesc: "Access session materials and notes",
      comingSoon: "Coming Soon",
    },
    mysteryCalls: {
      title: "Mystery Calls",
      description: "Your mystery call hub - join calls, complete forms, and access recordings",
      joinCall: "Join Mystery Call",
      joinCallDesc: "Join your scheduled mystery call",
      selfAssessment: "Your Self-Assessment",
      selfAssessmentDesc: "Complete your self-assessment form",
      guestFeedback: "Guest Feedback",
      guestFeedbackDesc: "Feedback from your mystery guest",
      pastRecordings: "Past Recordings",
      pastRecordingsDesc: "Watch your past mystery calls",
      scheduleCall: "Update Your Calendar",
      scheduleCallDesc: "Set your availability for next month",
      introduction: "Introduction",
      introductionDesc: "Get started with mystery calls",
      meetTheTeam: "Meet the Team",
      meetTheTeamDesc: "Get to know your mystery call partners",
      comingSoon: "Coming Soon",
    },
    monthlyMission: {
      title: "Monthly Mission",
      noMission: "No mission assigned yet",
      coachWillAssign: "Your coach will assign you a mission soon!",
      uploadDocument: "Upload Document",
      pastMissions: "Past Missions",
      soon: "Soon",
      yourUploads: "Your Uploads",
      feedback: "Feedback",
      loadingMission: "Loading mission...",
      uploadSuccess: "File uploaded successfully!",
      uploadFailed: "Failed to upload file",
    },
    contact: {
      title: "Contact Me",
      description: "Ask a question or share a recommendation",
      selectType: "Select message type",
      question: "Question",
      recommendation: "Recommendation",
      other: "Other",
      placeholder: "Type your message here...",
      sending: "Sending...",
      sendMessage: "Send Message",
      messageSent: "Message sent successfully!",
      messageFailed: "Failed to send message",
      enterMessage: "Please enter a message",
    },
    personalNotes: {
      title: "My Personal Notes",
      description: "Write notes for yourself",
      placeholder: "Write your personal notes here...",
      saving: "Saving...",
      saveNote: "Save Note",
      noteSaved: "Note saved!",
      noteFailed: "Failed to save note",
      enterNote: "Please enter a note",
      loadingNotes: "Loading notes...",
    },
    accent: {
      title: "Accent Challenge",
      subtitle: "Upload your pronunciation recordings for feedback and track your progress.",
      backToResources: "Back to Resources",
      submitRecording: "Submit a Recording",
      selectModule: "Select Module",
      chooseModule: "Choose a module",
      audioFile: "Audio File",
      audioFileDesc: "Upload your pronunciation recording (MP3, WAV, M4A)",
      notes: "Notes (Optional)",
      notesPlaceholder: "Add any notes about your recording...",
      submit: "Submit Recording",
      submitting: "Submitting...",
      yourRecordings: "Your Recordings",
      noRecordings: "No recordings yet. Upload your first recording to get feedback!",
      loading: "Loading your recordings...",
      delete: "Delete",
      deleting: "Deleting...",
    },
    accentCourse: {
      pageTitle: "Accent Challenge",
      pageDescription: "Boost your French accent in 6 weeks with daily practice exercises",
      backToAccentChallenge: "Back to Accent Challenge",
      yourProgress: "Your Progress",
      // Nav items
      navCourseOverview: "Course Overview",
      navCourseOverviewDesc: "Discover what the Accent Challenge is all about",
      navInitialTest: "Initial Test",
      navInitialTestDesc: "Identify your pronunciation strengths and weaknesses",
      navWeek: "Week",
      // Course overview slides
      slideIntroTitle: "Introduction",
      slideIntroDesc: "What is the Accent Challenge?",
      slideHowItWorksTitle: "How it works",
      slideHowItWorksDesc: "How to complete your challenge",
      slideHowItWorksStep1: "Complete an initial test to spot your weakest areas.",
      slideHowItWorksStep2: "Each week, access one challenge to complete.",
      slideHowItWorksStep3: "Lessons and practice exercises help you train first.",
      slideHowItWorksStep4: "Use the recording tool to submit and receive feedback.",
      slideHowItWorksHint: "Complete the Initial Test to unlock Week 1.",
      slideDocumentsTitle: "Course documents",
      slideDocumentsDesc: "Download the documents to track your progress",
      slideDocumentsCourseDetails: "Download course details",
      slideDocumentsDiary: "Download the Accent Challenge diary",
      slideDocumentsGoToTest: "Go to Initial Test",
      // Carousel nav
      previous: "Previous",
      next: "Next",
      // Initial test
      initialTestDone: "Initial test completed! Week 1 is now unlocked.",
      goToWeek1: "Go to Week 1: Vowel Victory",
      // Lock messages
      submitDayRecordingToContinue: "Submit your Day {n} recording to continue",
      submitMissionRecordingToContinue: "Submit your Mission recording to continue",
      unlocksAt: "Unlocks at {time}",
      completeTodayToUnlockNextDay: "Complete today's recording to unlock the next day.",
      completeTodayToUnlockMission: "Complete today's recording to unlock the mission.",
      completePrevWeekMission: "Complete the previous week's mission to unlock",
      readyToUnlock: "Ready to unlock!",
      // Day carousel slides
      day: "Day",
      missionPreviewTitle: "Discover the Mission — Week {n}",
      missionPreviewDesc: "Take a moment to read through the mission document. This is just a preview so you know what to aim for this week. You will complete and record the mission on day 7, after finishing all daily lessons.",
      openMissionDoc: "Open the mission document",
      lessonOpenHint: "Open the lesson, then practise the exercises before moving on.",
      openLessonOfDay: "Open the lesson of the day",
      tipOfTheDay: "Tip of the Day",
      tipOfTheDayAlt: "Tip of the Day {n}",
      recordPracticeHint: "Record your Day {n} practice and submit for feedback.",
      // Mission submit
      completeMissionTitle: "Complete the Mission — Week {n}",
      completeMissionDesc: "You've completed all 6 days of practice! Review the mission document, then record your submission.",
      recordMissionHint: "Record and submit your mission practice below.",
      weekCompleteTitle: "Week {n} Complete!",
      weekCompleteFinalDesc: "Congratulations! You've completed the entire Accent Challenge. Well done!",
      weekCompleteDesc: "Great job completing this week's mission! Your coach will review your recording.",
      goToWeekN: "Go to Week {n}",
      weekNUnlocksSoon: "Week {n} — unlocks soon",
      // Recording section
      recordingsTitle: "Your recordings",
      recordingsDescription: "Record or upload your pronunciation exercises",
      recordingTitlePlaceholder: "Recording title (optional)",
      record: "Record",
      stopRecording: "Stop recording",
      uploadFile: "Upload a file",
      uploading: "Uploading...",
      yourRecordingsLabel: "Your recordings:",
      rename: "Rename",
      download: "Download",
      defaultRecordingName: "Recording {n}",
      // Toasts
      toastError: "Error",
      toastCannotLoadRecordings: "Could not load recordings",
      toastRecordingSaved: "Recording saved",
      toastRecordingSavedDesc: "Your recording was saved successfully",
      toastCannotSaveRecording: "Could not save recording",
      toastRecordingInProgress: "Recording in progress",
      toastSpeakNow: "Speak now...",
      toastCannotAccessMic: "Could not access the microphone",
      toastRecordingAdded: "Recording added",
      toastRecordingAddedDesc: "Your audio file was added successfully",
      toastCannotUploadFile: "Could not upload the file",
      toastRecordingDeleted: "Recording deleted",
      toastCannotDeleteRecording: "Could not delete recording",
      toastCannotDownloadRecording: "Could not download recording",
      toastTitleUpdated: "Title updated",
      toastTitleUpdatedDesc: "Your recording title was updated successfully",
      toastCannotUpdateTitle: "Could not update title",
      // Rename dialog
      renameDialogTitle: "Rename recording",
      renameDialogDesc: "Edit the title of your recording",
      newTitle: "New title",
      cancel: "Cancel",
      save: "Save",
      // Coach feedback (admin)
      coachFeedback: "Coach Feedback",
      statusPending: "Pending",
      statusReviewed: "Reviewed",
      statusNeedsRevision: "Needs revision",
      lastUpdated: "Last updated: {date}",
      noFeedbackYet: "No feedback yet",
      status: "Status",
      pronunciationComments: "Pronunciation comments",
      feedbackPlaceholder: "Enter your comments on pronunciation, areas to improve, and the learner's wins...",
      feedbackTip: "💡 Tip: Mention specific timestamps (e.g. \"At 0:15, the 'u' sound...\") for more precise feedback",
      sendFeedback: "Send feedback",
      saving: "Saving...",
      enterAComment: "Please enter a comment",
      feedbackSaved: "Feedback saved",
      feedbackSavedDesc: "Your comment was saved successfully",
      cannotSaveFeedback: "Could not save feedback",
      // Learner feedback view
      feedbackPendingMessage: "Your recording is awaiting review by your coach.",
      yourCoachFeedback: "Feedback from your coach",
      receivedOn: "Received on {date}",
    },
    conjugation: {
      title: "Verb Echo",
      backToResources: "Back to Resources",
      configuration: "CONFIGURATION",
      description: "Master French verb conjugations with our interactive trainer featuring the top 250 most common French verbs. Practise different tenses and moods in both structured and random modes to build your confidence and fluency.",
      selectVerbs: "Choose your verb",
      searchVerbs: "Search verbs...",
      selectTenses: "Choose your tense",
      continue: "Continue",
      modeSelection: "TRAINING MODE",
      chooseMode: "Choose a mode",
      structuredMode: "Structured Practise",
      structuredDesc: "Practise one verb at a time, going through all selected tenses systematically.",
      randomMode: "Random Challenge",
      randomDesc: "Get random combinations of verbs and tenses for varied practise.",
      startTraining: "Start Training",
      specialChars: "Special characters:",
      checkAnswers: "Check Answers",
      nextExercise: "Next Exercise",
      finishTraining: "Finish Training",
      results: "Results",
      finalScore: "Your final score:",
      startOver: "Start Over",
      loginRequired: "Login Required",
      flashcardSaved: "Flashcard Saved!",
      flashcardSavedDesc: "This verb has been added to your review deck",
      errorSaving: "Error Saving Flashcard",
      saveFlashcard: "Save to Flashcards",
      reviewFlashcards: "Review Flashcards",
      focusMode: "Focused practice on your weakest tenses",
      focusingOn: "Focusing on",
      conjugateVerb: "Conjugate this verb",
      fillConjugation: "Fill in the correct conjugation",
      verb: "Verb",
      tense: "Tense",
      pronoun: "Pronoun",
      typeAnswer: "Type your answer...",
      enterAnswer: "Please enter an answer",
      correct: "Correct!",
      incorrect: "Incorrect.",
      checkAnswer: "Check Answer",
      score: "Score",
      subtitle: "Practice French verb conjugations",
    },
    flashcards: {
      loading: "Loading flashcards...",
      noDueCards: "No Cards Due for Review",
      noDueCardsDesc: "Great work! All your flashcards are up to date. Practice more in the conjugation trainer to add new cards.",
      back: "Back to Trainer",
      showAnswer: "Show Answer",
      ratePerformance: "How well did you know this?",
      tryRecall: "Try to recall all conjugations before showing the answer",
      sessionComplete: "Review session complete!",
      correct: "Correct",
      accuracy: "Accuracy",
      errorLoading: "Error loading flashcards",
      errorUpdating: "Error updating flashcard",
      errorAudio: "Audio playback error",
    },
    stats: {
      title: "Verb Practice Statistics",
      subtitle: "Track your progress and identify areas for improvement",
      dashboardIntro: "Master French verb conjugations with targeted practice. Start a new session or review your progress below.",
      goToConfiguration: "Start New Practice Session",
      loading: "Loading statistics...",
      backToTrainer: "Back to Trainer",
      backToResources: "Back to Resources",
      backToDashboard: "Back to The 3 Pillars",
      focusWeakTenses: "Focus on Weak Tenses",
      focusWeakTensesDesc: "Practice tenses you struggle with most",
      practiceAll: "Practice All Tenses",
      practiceAllDesc: "Random practice across all verb tenses",
      startFocused: "Start Focused Practice",
      totalAttempts: "Total Attempts",
      conjugationsPracticed: "conjugations practiced",
      overallAccuracy: "Overall Accuracy",
      tensesPracticed: "Tenses Practiced",
      differentTenses: "different tenses",
      recentActivity: "Recent Activity",
      active: "Active",
      inactive: "Inactive",
      last30Days: "Last 30 days",
      byTense: "By Tense",
      progress: "Progress",
      byVerb: "By Verb",
      tenseDifficulty: "Tense Difficulty Overview",
      tenseDifficultyDesc: "See which tenses you find most challenging",
      mostChallenging: "Most Challenging Tenses",
      mostChallengingDesc: "Focus on these for improvement",
      accuracyOverTime: "Accuracy Over Time",
      accuracyOverTimeDesc: "Track your improvement over the last 30 days",
      topVerbs: "Top Performing Verbs",
      topVerbsDesc: "Verbs you've mastered",
      strugglingVerbs: "Needs Practice",
      strugglingVerbsDesc: "Focus on these verbs",
      attempts: "attempts",
      correct: "Correct",
      incorrect: "Incorrect",
      accuracy: "Accuracy",
      noDataYet: "No practice data yet. Start training to see your stats!",
      keepPracticing: "Keep Practicing!",
      keepPracticingDesc: "The more you practice, the better your stats will look",
      startTraining: "Start Training",
      errorLoading: "Error loading statistics",
    },
    notFound: {
      title: "404",
      message: "Oops! Page not found",
      returnHome: "Return to Home",
    },
    aiConversation: {
      title: "AI Conversation Practice",
      subtitle: "Practice speaking French with AI. Click the microphone to start speaking!",
      backToResources: "Back to Resources",
      version1Notice: "This is version 1.0 with basic text-to-speech. Improved, more natural voices will be available soon! Feel free to start practicing now - your conversation skills will transfer perfectly to the enhanced version.",
      scenarioLabel: "Scenario",
      difficultyLabel: "Difficulty Level",
      generalConversation: "General Conversation",
      restaurant: "Restaurant",
      travel: "Travel",
      shopping: "Shopping",
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
      clickToStart: "Click the microphone below to start your conversation in French",
      you: "You",
      aiTutor: "AI Tutor",
      thinking: "Thinking...",
      stopListening: "Stop Listening",
      startSpeaking: "Start Speaking",
      stopSpeaking: "Stop Speaking",
      newConversation: "New Conversation",
      listening: "Listening... Speak in French",
      tipsTitle: "Tips for Better Conversation:",
      tip1: "Speak clearly and at a normal pace",
      tip2: "Use Chrome or Edge browser for best results",
      tip3: "Make sure your microphone is enabled",
      tip4: "The AI will respond in French at your chosen level",
      tip5: "Select different scenarios to practice specific situations",
      history: "History",
      analytics: "Analytics",
      newConversationStarted: "Starting fresh! Click the microphone to begin.",
      sessionLoaded: "Previous conversation loaded successfully.",
    },
    conversationHistory: {
      title: "Recent Conversations",
      loading: "Loading history...",
      noHistory: "No conversation history yet. Start a conversation to see it here!",
      messages: "messages",
      load: "Load",
      deleted: "Conversation session deleted",
      deleteError: "Failed to delete session",
    },
    conversationAnalytics: {
      loading: "Loading analytics...",
      noData: "No conversation data yet. Start practicing to see your analytics!",
      conversations: "Conversations",
      speakingTime: "Speaking Time",
      totalMessages: "Total Messages",
      avgMessages: "Avg. Messages",
      min: "min",
      mostPracticed: "Most Practiced Scenarios",
      activityLast30Days: "Activity Over Last 30 Days",
      practiceByDifficulty: "Practice by Difficulty Level",
      general: "General",
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
    },
    dashboard: {
      welcome: "Salut",
      ready: "Ready for your next session?",
      loading: "Loading...",
    },
    index: {
      welcomeTitle: "Welcome to Le Coin Étude!",
      welcomeIntro1: "Here, you'll find everything you need to succeed on your Quirky French journey. Access a rich library of learning resources you can explore at your own pace, along with all the teaching materials we use in our sessions together.",
      welcomeIntro2: "Plus, find everything you need in your 3 Pillars: your links to our 1-1 sessions, Mystery Call links and files, it's all in one convenient place now! But there's more, so have a little explore and don't hesitate to message me if you have a question, comment, or suggestion!",
      welcomeIntro3: "On y va! Let's do it!",
      latestUpdates: "Latest Updates",
      noUpdates: "No updates available at the moment.",
      comingSoon: "Coming Soon",
      scheduleMystery: "Schedule Mystery Calls",
      scheduleMysterydesc: "Book your future mystery calls directly from the platform",
      mysteryAssessment: "Mystery Call Assessment",
      mysteryAssessmentDesc: "Complete self-assessments and view your tutor's feedback",
      selfPacedCourses: "Self-Paced Courses",
      selfPacedCoursesDesc: "Complete courses online and receive personalised tutor feedback",
      progressTracking: "Progress Tracking",
      progressTrackingDesc: "Monitor your learning journey with detailed progress insights",
    },
    worksheet: {
      checklist: "Checklist",
      reflection: "Reflection",
      personalNotes: "Personal notes",
      optional: "(optional)",
      notesPlaceholder: "Jot down any additional thoughts…",
      thoughtsPlaceholder: "Write your thoughts here…",
      saveProgress: "Save progress",
      markComplete: "Mark as complete",
      completed: "Completed",
      moduleCompleted: "Step completed",
      progressSaved: "Progress saved",
      progressSavedDesc: "Your responses have been saved successfully",
      moduleCompletedDesc: "Great work! Your completion has been recorded.",
      authRequired: "Authentication required",
      authRequiredDesc: "Please sign in to save your progress",
      errorSaving: "Error saving",
      errorSavingDesc: "Failed to save your progress",
    },
    voiceLab: {
      recordTitle: "Record Your Practice",
      recordDesc: "Record using your microphone or upload an existing audio file",
      clickToRecord: "Click to start recording",
      or: "or",
      uploadAudioFile: "Upload Audio File",
      uploadFormats: "MP3, WAV, M4A, WEBM, or OGG (max 20MB)",
      recordingInProgress: "Recording in progress...",
      audioReady: "Audio ready for upload",
      previewSubmit: "Preview and submit below",
      previewRecording: "Preview Recording",
      reRecord: "Re-record",
      recordingName: "Recording Name *",
      recordingNamePlaceholder: "e.g., Nasal sounds practice, R pronunciation",
      notesOptional: "Notes (optional)",
      notesPlaceholder: "Add any notes or questions for your coach...",
      uploading: "Uploading...",
      uploadRecording: "Upload Recording",
      myRecordings: "My Recordings",
      myRecordingsDesc: "Track your submitted recordings and view feedback",
      noRecordings: "No recordings yet. Start recording to get feedback!",
      pending: "Pending",
      reviewed: "Reviewed",
      notes: "Notes",
      noNotes: "No notes",
      yourFeedback: "Your Feedback",
      waitingFeedback: "Waiting for feedback",
      addNotes: "Add notes...",
      playback: "Recording Playback",
      close: "Close",
      errorMic: "Error accessing microphone",
      invalidFile: "Invalid file type",
      invalidFileDesc: "Please upload an audio file (MP3, WAV, M4A, WEBM, or OGG)",
      fileTooLarge: "File too large",
      fileTooLargeDesc: "Please upload a file smaller than 20MB",
      fileUploaded: "File uploaded",
      fileUploadedDesc: "You can now preview and submit your recording",
      missingInfo: "Missing information",
      missingInfoDesc: "Please record or upload audio and provide a recording name",
      recordingUploaded: "Recording uploaded",
      recordingUploadedDesc: "Your coach will review it and provide feedback soon",
      errorUploading: "Error uploading recording",
      errorLoading: "Error loading recordings",
      errorLoadingRecording: "Error loading recording",
      errorDownloading: "Error downloading recording",
      nameRequired: "Name is required",
      recordingUpdated: "Recording updated",
      errorUpdating: "Error updating",
    },
    conversationPractice: {
      title: "Let's chat?",
      description: "Talk about whatever you like, in French. No script, no pressure — just a conversation.",
      myConversations: "My conversations",
      level: "Level",
      timerAria: "Elapsed time",
      topicChip: "Topic",
      topicLabel: "Topic",
      topicComingSoonTitle: "Coming soon",
      topicFree: "free",
      topicPickerHeading: "Pick a topic",
      topicFreeOption: "Free conversation (no topic)",
      topicCustomLabel: "Custom topic",
      topicCustomPlaceholder: "e.g. my weekend",
      topicComingSoonDesc: "This is where you'll be able to pick the topic of the month to focus your practice. Stay tuned!",
      preferences: "Settings",
      preferencesAria: "Preferences",
      restart: "Start over",
      restartAria: "Start over",
      comeBackTomorrowTitle: "Come back tomorrow",
      comeBackTomorrowDesc: "You've already done your daily session. A new conversation will be available tomorrow.",
      // Preferences panel
      modeLabel: "Mode",
      modeFluency: "Flow",
      modeFluencyDesc: "Natural turn-taking. Lola detects your pauses and replies on her own.",
      modeComfort: "Comfort",
      modeComfortDesc: "Re-listen and re-record before sending to Lola.",
      modeHint: "Flow: natural turn-taking — Lola detects your pauses and replies on her own. Comfort: re-listen and re-record before sending.",
      pauseAutoHint: "Pause length adapts automatically (~{seconds} s) — supportive Lola waits longer, demanding Lola interrupts sooner.",
      personalityLabel: "AI personality",
      personalitySupportive: "Supportive",
      personalitySupportiveDesc: "Very patient. Checks in, suggests words, actively helps.",
      personalityNeutral: "Neutral",
      personalityNeutralDesc: "Like a friendly native. Up to you to clarify if needed.",
      personalityJudgemental: "Demanding",
      personalityJudgementalDesc: "In a hurry, not very patient. Forces you to be clear.",
      personalityHint: "Changes how the AI reacts to hesitations and unclear sentences.",
      intensityLabel: "Character intensity",
      intensityOff: "Off",
      intensitySubtle: "Subtle",
      intensityBalanced: "Balanced",
      intensityMarked: "Marked",
      intensityAria: "Voice character intensity",
      intensityHintJudgemental: "The higher you go, the more the AI sighs and drops sarcastic jabs.",
      intensityHintSupportive: "The higher you go, the warmer the AI gets and the more it laughs softly.",
      intensityHintNeutral: "The higher you go, the more pronounced the AI's thoughtful pauses.",
      vibeLabel: "Vibe",
      vibeAria: "Lola's vibe — from warmly supportive to sharply demanding",
      vibeAnchorSupportive: "Supportive",
      vibeAnchorNeutral: "Neutral",
      vibeAnchorDemanding: "Challenging",
      vibeHint: "Sets Lola's energy — supportive (warm, encouraging, gentle pace) on the left, challenging (more demanding, faster, pushes you to stretch) on the right.",
      transcriptLabel: "Text display",
      transcriptShown: "Transcript",
      transcriptHidden: "Text hidden (ear mode)",
      transcriptHint: "Hide the text to train yourself to understand by ear only.",
      // Levels
      levelA1: "A1 · Beginner",
      levelA2: "A2 · Elementary",
      levelB1: "B1 · Intermediate",
      levelB2: "B2 · Upper intermediate",
      levelC1C2: "C1/C2 · Advanced",
      // Status messages
      reviewHint: "Listen back, re-record if needed, then send your best take.",
      cancel: "Cancel",
      sendToAI: "Send to AI",
      reviewTakeLabel: "Take",
      reviewTakeLatest: "latest",
      reviewTakePrevious: "previous",
      reviewPlay: "Play",
      reviewPlaying: "Playing…",
      reviewSlow: "Slow (0.75x)",
      reviewReRecord: "Re-record",
      reviewSendThis: "Send this take",
      reviewShowTranscript: "Show transcript",
      reviewHideTranscript: "Hide transcript",
      reviewTranscriptHint: "What was understood (read-only). If something's off, re-record rather than edit.",
      replayLolaLine: "Replay Lola's last line",
      needAHand: "Need a hand?",
      needAHandHint: "A few ways to start your reply — pick one, then continue in your own words.",
      startersLoading: "Thinking of a few starters…",
      startersError: "Couldn't load starters. Try again in a moment.",
      startersHide: "Hide suggestions",
      ended: "Conversation ended",
      endedDailyLimit: "You've reached your daily 6-minute limit. Come back tomorrow for a new conversation — meanwhile, you can re-read the exchange or download the transcript.",
      endedNormal: "You can re-read the conversation above, download the transcript, or start a new one.",
      downloadTranscript: "Download transcript & feedback",
      downloadAudio: "Download audio (.wav)",
      audioDownloadEmptyTitle: "No audio to download",
      audioDownloadEmptyDesc: "Audio is only available right after the conversation ends. Refreshing the page clears it.",
      audioDownloadFailedTitle: "Audio download failed",
      audioDownloadFailedDesc: "Couldn't stitch the conversation audio.",
      comeBackTomorrowBtn: "Come back tomorrow",
      newConversation: "New conversation",
      feedbackLoading: "Lola is preparing your feedback…",
      feedbackErrorTitle: "Feedback unavailable",
      feedbackErrorDesc: "Couldn't generate Lola's feedback this time. You can still download the transcript.",
      feedbackHeading: "Lola's debrief",
      feedbackPlayAgain: "Play again",
      feedbackPlaying: "Playing…",
      feedbackOverall: "Overall",
      feedbackReformulations: "Reformulations",
      feedbackReformulationsHint: "Tap a line to hear Lola say it.",
      feedbackOriginalLabel: "You said",
      feedbackImprovedLabel: "Try this",
      feedbackReachedFor: "Words you reached for",
      feedbackNextSteps: "Next time, try this",
      feedbackFillerTitle: "Hesitation rate",
      feedbackFillerPer100: " /100 words",
      feedbackFillerSuggested: "Try these French markers instead",
      feedbackDownloadNudge: "Save it all — transcript + feedback — as a PDF you can revisit:",
      startConversationAria: "Start the conversation",
      hardCapHint: "6-min limit reached — the conversation is over. Click \"End\" to grab the transcript.",
      recordingFluency: "I'm listening… stop naturally and I'll take over.",
      recordingComfort: "Recording… click to stop.",
      transcribing: "Transcribing…",
      thinking: "The AI is thinking…",
      idleNotStarted: "When you're ready, click to start the conversation.",
      idleSoftCap: "We're nearing the end — the AI will wrap up. You can have one last word.",
      idleResumeFluency: "Click to take the floor again — then it flows on its own.",
      idleResumeComfort: "Click to speak.",
      endConversation: "End conversation",
      wrapUpWithLola: "Wrap up with Lola",
      wrapUpHint: "Lola will give you spoken feedback the moment you tap this — sit tight and listen.",
      wrappingUpTitle: "Lola is wrapping up…",
      wrappingUpDesc: "She's about to share her thoughts on your conversation. Listen out.",
      softWarningTitle: "Almost time to wrap up",
      softWarningDesc: "About 30 seconds left — Lola will close the conversation soon.",
      softWarningCaption: "Almost time — Lola will wrap up shortly.",
      replay: "Replay",
      replaying: "Playing…",
      replayMyVoiceAria: "Replay my voice",
      // Toasts
      audioPlaybackError: "Audio playback failed",
      audioPlaybackErrorDesc: "Could not generate audio.",
      insecureContextTitle: "Insecure connection",
      insecureContextDesc: "The mic requires an HTTPS connection. Reload the page on https://.",
      browserNotSupportedTitle: "Browser not supported",
      browserNotSupportedDesc: "Your browser doesn't support audio recording. Try the latest Safari (iOS) or Chrome (Android).",
      mediaRecorderUnsupportedDesc: "Your browser doesn't support MediaRecorder. Update it or try another browser.",
      tooShortTitle: "Too short",
      tooShortDesc: "Try speaking a bit longer.",
      nothingCapturedTitle: "Didn't catch that",
      nothingCapturedDesc: "We couldn't transcribe — try again, speaking more clearly.",
      transcriptionError: "Transcription error",
      genericErrorTitle: "Error",
      aiUnreachable: "Could not reach the AI.",
      dailyLimitTitle: "Daily limit reached",
      dailyLimitDesc: "You've hit the 6 minutes of conversation. Come back tomorrow for a new session!",
      downloadFailedTitle: "Download failed",
      downloadFailedDesc: "Could not generate the PDF.",
      // Mic errors
      micUnavailableTitle: "Mic unavailable",
      micUnavailableDesc: "Check your browser permissions.",
      micPermissionDeniedTitle: "Mic permission denied",
      micPermissionDeniedIos: "On iPhone: open Settings → Safari → Microphone → allow. Then come back here, reload the page (the ↻ icon at the bottom) and try again. If it still doesn't work, fully close Safari and reopen it.",
      micPermissionDeniedDefault: "Tap the lock icon 🔒 in the address bar, allow the mic, then reload the page.",
      micNoneTitle: "No mic detected",
      micNoneDesc: "No mic is plugged in or available on this device.",
      micBusyTitle: "Mic busy",
      micBusyDesc: "Another app is already using the mic (call, WhatsApp, etc.). Close it and try again.",
      micIncompatibleTitle: "Incompatible mic",
      micIncompatibleDesc: "The audio constraints are not supported on this device.",
      micBlockedTitle: "Blocked for security reasons",
      micBlockedDesc: "The browser is blocking the mic. Make sure you're on a secure (https) page.",
      reloadPage: "Reload the page",
      close: "Close",
    },
    errors: {
      notSupported: "Not Supported",
      browserNotSupported: "Your browser doesn't support speech recognition. Please use Chrome or Edge.",
      error: "Error",
      errorLoading: "Error loading resources",
      errorLoadingAnalytics: "Failed to load analytics data",
      recognitionError: "Recognition Error",
      recognitionFailed: "Could not understand. Please try again.",
      voiceRecognitionError: "Could not start voice recognition.",
      responseFailed: "Failed to get response. Please try again.",
    },
    common: {
      comingSoon: "Coming Soon",
      letsGo: "Let's go",
      letsDiveIn: "Let's dive in",
      loading: "Loading...",
      back: "Back",
      open: "Open",
    },
    dashboardExt: {
      monthlyMission: "Monthly Mission",
      taglineCoaching: "Your private training ground",
      taglineMystery: "Step outside the classroom",
      taglineMission: "Every month, we raise the bar",
      coachingHook1: "Deep work. Clear breakthroughs. Real progress.",
      coachingHook2: "This is where we refine your language, sharpen your thinking, and build the confidence that actually lasts.",
      mysteryHook1: "Different guest. New scenario. Zero predictability.",
      mysteryHook2: "Speak. Adapt. Survive. Grow.",
      missionHook1: "No script. No safety net. Just real conversation.",
      missionHook2: "This is where confidence is built for real.",
      thisMonthsMission: "This Month's Mission",
      thisMonthsMissionDesc: "Your monthly challenge to push your French to the next level.",
      accessDocuments: "Access Documents",
      watchRecordings: "Watch Recordings",
      viewFeedback: "View Feedback",
      workspaceNotSetUp: "Your workspace link hasn't been set up yet.",
      coachAlt: "Your coach",
    },
    mysteryCallsPage: {
      pageTitle: "Mystery Calls",
      pageSubtitle: "Introduction",
      stepBeforeTitle: "Before the mystery call",
      stepBeforeDesc: "Prepare yourself for success",
      stepDuringTitle: "During the mystery call",
      stepDuringDesc: "What to do while on the call",
      stepAfterTitle: "After the mystery call",
      stepAfterDesc: "Reflect and track your progress",
      stepGuideTitle: "Quick guide",
      stepGuideDesc: "A handy reference document",
      guideHeading: "Quick Guide",
      guideBlurb: "A handy reference document summarising everything you need to know about mystery calls.",
      openGuide: "Open the quick guide",
      coursePlayerDesc: "Discover the mystery calls!",
      schedulePageTitle: "Mystery Call Scheduling",
      schedulePageDesc: "Select your available time slots for upcoming mystery calls",
      scheduleAutoOpen: "The scheduling calendar should open automatically. If it doesn't, please refresh the page.",
    },
    mysteryStatusCalendar: {
      available: "Available",
      scheduled: "Scheduled",
      toReschedule: "To be rescheduled",
      completed: "Completed",
      cancelled: "Cancelled",
      tooltipCancelled: "Cancelled",
      tooltipToReschedule: "To reschedule – was",
      tooltipCompletedAt: "Completed at",
      tooltipScheduledAt: "Scheduled at",
    },
    indexExt: {
      stayInformed: "Stay informed",
      whatsNext: "What's next",
    },
    resourcesExt: {
      taglineInduction: "Start here",
      taglineConfidence: "Be yourself, in French",
      taglinePhonetics: "Play with sounds",
      taglineVocabulary: "Words that stick",
      taglineFluency: "From choppy to flowing",
      taglinePodcast: "Learn on the go",
      taglineGrammar: "Rules that click",
      taglineListening: "Train your ear",
      // Card titles
      findYourFocusTitle: "Find Your Focus",
      yourCoursePathwayTitle: "Your Course Pathway",
      letsChatTitle: "Let's chat!",
      blueprintTitle: "The Blueprint",
      grammarPatternsTitle: "Grammar Patterns",
      podcastFreebiesTitle: "Podcast Freebies",
      uhdListeningTitle: "UHD Listening Drills",
      dictationPracticeTitle: "Dictation Practice",
      speedListeningTitle: "Speed Listening",
      // Card descriptions
      descSkillOfLearning: "Discover how to learn languages more effectively with proven strategies.",
      descFindYourFocus: "Find your focus area and get a personalised learning path.",
      descFocusProfile: "Your personalised course pathway based on your focus area.",
      descYourCoursePathway: "Get a custom course pathway tailored to your needs. Find your focus first!",
      descConfidenceDiary: "Build unshakable confidence with daily reflection exercises.",
      descStayingConnected: "Stay connected to French even on your busiest days.",
      descConfidencePlaylist: "Listen to episodes designed to boost your speaking confidence.",
      descPronunciationRecorder: "Record yourself and get feedback on your pronunciation.",
      descAccentChallenge: "Transform your accent with guided daily challenges.",
      descPhoneticsCorrection: "Fine-tune the sounds that trip you up the most.",
      descVocabStartHere: "Your starting point to build a strong vocabulary foundation.",
      descTopicsOfMonth: "Fresh themed vocabulary to keep your learning dynamic.",
      descVocabPlaylist: "Expand your word bank with curated audio content.",
      descPassiveToActive: "Transform words you understand into words you use.",
      descLetsChat: "A free-flowing French conversation partner — no scenario, no script. Just talk.",
      descPathToFluency: "Begin your journey towards natural, flowing French.",
      descVerbEcho: "Build verb reflexes through listen-and-respond drills.",
      descBlueprint: "Build French sentences from the ground up with guided exercises.",
      descConjugationTrainer: "Train your verb conjugations until they become second nature.",
      descGrammarPatterns: "Decode French grammar patterns and make them yours.",
      descPodcast: "Tune in to episodes crafted to boost your French naturally.",
      descPodcastFreebies: "Grab free worksheets and resources to go with each episode.",
      descUhdListening: "Sharpen your ear with immersive listening exercises.",
      descDictationPractice: "Test your comprehension by writing what you hear.",
      descSpeedListening: "Train your brain to process French at natural speed.",
      // Pathway dialog
      pathwayDialogTitle: "Your Course Pathway",
      pathwayDialogPara1: "This is where you'll get a personalised course pathway designed to help you pick the best courses for your needs.",
      pathwayDialogPara2: "But first, let's identify your focus area — it only takes a few minutes.",
      pathwayDialogMaybeLater: "Maybe later",
      pathwayDialogFindMyFocus: "Find My Focus",
      // Focus profile titles
      focusSpeakingStructureTitle: "Strengthen Patterns & Structure",
      focusListeningTitle: "Sharpen Listening & Comprehension",
      focusAnxietyTitle: "Build Conversation Confidence",
      focusAccentTitle: "Refine Your Accent",
      // Misc toast
      notAvailableYet: "Not available yet",
      noChatgptLink: "Your coach hasn't assigned a conversation practice link yet.",
    },
    billingExt: {
      title: "Billing",
      loadingState: "Loading...",
      manageDescription: "Manage your subscription and payment methods.",
      activateDescription: "Activate your subscription to access your space.",
      activateButton: "Activate my subscription",
      grandfatheredDesc: "Your access is active. Save your card to be charged automatically on the 1st of each month.",
      unlimitedAccess: "Unlimited access",
      unlimitedAccessDesc: "No change required for now. Carry on as before.",
      switchToAuto: "Switch to automatic payment",
      switchToAutoDescPart1: "Save your card today —",
      switchToAutoDescPart2: "no immediate charge",
      switchToAutoDescPart3: "Your first automatic payment of",
      switchToAutoDescPart4: "will take place on",
      switchToAutoDescPart5: "then on the 1st of each month.",
      saveMyCard: "Save my card",
      noChargeToday: "No charge today. First payment on",
      accessSuspended: "Access suspended",
      accessSuspendedDesc: "Settle your payment to regain full access to the platform.",
      plan: "Plan",
      accessUntil: "Access until",
      nextRenewal: "Next renewal",
      manageSubscription: "Manage my subscription",
      planNotAssigned: "Plan not assigned",
      planNotAssignedDesc: "Contact Nahéma to be assigned a plan.",
      portalError: "Error",
      portalFailed: "Failed to open portal",
      statusActive: "Active",
      statusTrialing: "Trial period",
      statusPastDue: "Payment overdue",
      statusCanceled: "Cancelled",
      statusIncomplete: "Incomplete",
      statusUnpaid: "Unpaid",
      statusPaused: "Paused",
      pausedTitle: "Payment paused",
      pausedDescWithDate: "No payment will be taken on your next billing date. Your access stays fully open, and billing resumes automatically on",
      pausedDescNoDate: "No payment will be taken for now. Your access stays fully open; billing resumes automatically later.",
      billingResumes: "Billing resumes on",
      cancelRequestButton: "Request to stop lessons",
      cancelRequestNote: "A one-month notice period applies.",
      cancelDialogTitle: "Request to stop lessons",
      cancelDialogIntro: "Your subscription includes a one-month notice period. Here is exactly what will happen:",
      cancelBullet1: "Final payment on",
      cancelBullet2: "Full access to lessons, resources and mystery calls until",
      cancelBullet3: "After that date, access ends and no further payment is taken.",
      cancelBullet4: "You and Nahéma both receive a confirmation email straight away.",
      cancelDialogBack: "Go back",
      cancelDialogConfirm: "Confirm my request",
      cancelScheduledTitle: "Cancellation scheduled",
      cancelScheduledDesc: "Your access ends on",
      cancelScheduledContact: "Changed your mind? Email Nahéma before that date.",
      cancelRequestDone: "Request registered",
      cancelRequestError: "Could not register your request",
      tier245: "£245 / month",
      tier270: "£270 / month",
      tier300: "£300 / month",
    },
    notificationsExt: {
      comingSoonSuffix: " (coming soon)",
    },
    profileSections: {
      password: {
        title: "Change Password",
        description: "Update your account password",
        currentPassword: "Current Password",
        newPassword: "New Password",
        confirmPassword: "Confirm New Password",
        updateButton: "Update Password",
        updating: "Updating...",
        enterPasswords: "Please fill in all password fields",
        minLength: "Password must be at least 6 characters",
        mismatch: "New passwords do not match",
        success: "Password updated successfully!",
        failed: "Failed to update password",
      },
      notifications: {
        title: "Notification Preferences",
        description: "Choose which notifications you'd like to receive",
        mysteryCalls: "Mystery Call reminders",
        general: "General updates and announcements",
        teacherFeedback: "Teacher feedback notifications",
        updateFailed: "Failed to update preference",
      },
      legal: {
        title: "Legal Documents",
        description: "Review our terms and policies",
        termsTitle: "Terms & Conditions",
        privacyTitle: "Privacy Policy",
        acceptance: "Acceptance of Terms",
        acceptanceText: "By accessing and using Le Coin Étude, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our services.",
        services: "Services",
        servicesText: "Le Coin Étude provides personalised French language coaching, including 1-1 sessions, Mystery Calls, Real-World Missions, and self-paced learning resources. Access to certain features may require an active subscription.",
        userObligations: "User Obligations",
        userObligationsText: "You agree to use the platform respectfully and in accordance with its intended purpose. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.",
        intellectualProperty: "Intellectual Property",
        intellectualPropertyText: "All content, materials, and resources provided on Le Coin Étude are the intellectual property of Quirky French and may not be reproduced, distributed, or used for commercial purposes without prior written consent.",
        limitation: "Limitation of Liability",
        limitationText: "Le Coin Étude is provided 'as is'. We strive to provide the best learning experience but cannot guarantee specific learning outcomes. We are not liable for any indirect or consequential damages arising from the use of our services.",
        dataCollection: "Data Collection",
        dataCollectionText: "We collect personal information you provide during registration (name, email, date of birth) and usage data such as learning progress, recordings, and session history to personalise your experience.",
        dataUse: "How We Use Your Data",
        dataUseText: "Your data is used to provide and improve our services, personalise your learning experience, communicate with you about your progress, and send relevant notifications based on your preferences.",
        dataProtection: "Data Protection",
        dataProtectionText: "We implement industry-standard security measures to protect your personal data. Your data is stored securely and is never sold to third parties. We comply with applicable data protection regulations.",
        yourRights: "Your Rights",
        yourRightsText: "You have the right to access, correct, or delete your personal data at any time. You can update your notification preferences in your profile settings. To request data deletion, please contact us directly.",
      },
      billing: {
        title: "Billing & Payments",
        description: "Manage your payment methods and view billing history",
        comingSoon: "Coming Soon",
        paymentMethods: "Payment Methods",
        paymentMethodsDesc: "Add and manage your payment methods",
        billingHistory: "Billing History",
        billingHistoryDesc: "View past invoices and transactions",
        subscription: "Subscription",
        subscriptionDesc: "Manage your active plan and subscription",
      },
    },
    assessment: {
      // Page header
      pageTitle: "Find Your Focus",
      pageDescription: "Find your focus area and get a personalised learning path.",
      backToResources: "Back to Resources",
      // Steps meta
      stepIntroTitle: "Before You Begin",
      stepIntroDesc: "A quick guide to your assessment",
      stepReflectTitle: "Reflect",
      stepReflectDesc: "Share what's on your mind",
      stepRateTitle: "Rate & React",
      stepRateDesc: "Rate each statement honestly",
      stepResultsTitle: "Your Focus",
      stepResultsDesc: "Your personalised result",
      // FocusIntroStep
      introHeading: "Find Your Fluency Focus",
      introMeta: "16 statements · takes about 3 minutes",
      introLine1: "This quick check helps us identify your current fluency focus.",
      introLine2: "There are no right or wrong answers.",
      introLine3: "Answer instinctively.",
      introCta: "Let's Begin",
      // Returning user view
      secondarySupport: "Secondary support",
      viewMyResults: "View My Results",
      generating: "Generating…",
      openFocusPathway: "Open Focus Pathway",
      retakeTest: "Retake the Test",
      pdfError: "Could not generate PDF.",
      // OpenEndedStep
      openEndedIntroHeading: "Take a quiet moment",
      openEndedIntroLine1: "The next three questions are open-ended reflections.",
      openEndedIntroLine2: "Just write what comes naturally — short answers are absolutely fine.",
      openEndedIntroLine3: "We'll use your responses to understand your learning profile.",
      openEndedPlaceholder: "Write freely…",
      openEndedQ1: "When you think about your French right now, what feels frustrating, heavy, or limiting?",
      openEndedQ2: "If those frustrations disappeared, how would speaking French feel instead?",
      openEndedQ3: "Who would you become in French if nothing held you back?",
      analysing: "Analysing…",
      analysisFailed: "Analysis failed. Moving to the next step.",
      continue: "Continue",
      // LikertCarousel
      likertNotTrue: "Not true for me",
      likertSlightly: "Slightly true",
      likertSomewhat: "Somewhat true",
      likertMostly: "Mostly true",
      likertVeryTrue: "Very true for me",
      answeredOf: "of",
      answered: "answered",
      previous: "Previous",
      next: "Next",
      seeMyResult: "See My Result",
      // Likert questions
      likertQ1: "I struggle to organise my thoughts quickly enough when I'm speaking.",
      likertQ2: "My sentences become complicated because I try to sound more advanced than I feel.",
      likertQ3: "I often start speaking before I'm clear on how to finish the sentence.",
      likertQ4: "I know the vocabulary, but I can't always structure it clearly in real time.",
      likertQ5: "I understand written French much better than spoken French.",
      likertQ6: "When someone speaks quickly, I lose track of the message.",
      likertQ7: "I need people to slow down or repeat themselves frequently.",
      likertQ8: "I feel mentally tired after listening to fast or informal French.",
      likertQ9: "I feel physical tension (heart rate, tight chest, shallow breathing) before speaking.",
      likertQ10: "I overthink what I'm about to say and second-guess myself.",
      likertQ11: "I replay conversations afterwards and criticise my performance.",
      likertQ12: "I avoid speaking situations even when I know I'm capable.",
      likertQ13: "I feel my pronunciation affects how confident I sound.",
      likertQ14: "French speakers sometimes struggle to understand me clearly.",
      likertQ15: "I find French sounds or rhythm difficult to reproduce naturally.",
      likertQ16: "I feel that my accent prevents me from sounding fully like myself.",
      // FocusResultsStep
      analysingResponses: "Analysing Your Responses",
      buildingProfile: "Building your personalised profile…",
      downloadPathway: "Download Pathway PDF",
      startThisFocus: "Start This Focus",
      focusSet: "Your focus area has been set!",
      saveError: "Something went wrong saving your result.",
      // Focus profile result titles & descriptions
      profileSpeakingResultTitle: "Your Primary Focus: Strengthen Patterns & Structure",
      profileSpeakingResultDesc: "You don't lack vocabulary. You need structure and decision speed.\nYour work will focus on simplifying, organising and building automatic speaking patterns.",
      profileListeningResultTitle: "Your Primary Focus: Sharpen Listening & Comprehension",
      profileListeningResultDesc: "Your challenge isn't your ability or your vocabulary. It's processing speed and listening stamina.\nWe'll train your brain to stay present and absorb fast speech without panic.",
      profileAnxietyResultTitle: "Your Primary Focus: Build Conversation Confidence",
      profileAnxietyResultDesc: "Your biggest barrier isn't language — it's how your body and mind react under pressure.\nWe'll work on staying calm, feeling like yourself in French, and building lasting conversational confidence.",
      profileAccentResultTitle: "Your Primary Focus: Refine Your Accent",
      profileAccentResultDesc: "Your fluency is stronger than you think.\nNow we refine clarity, rhythm and sound to increase presence and confidence.",
    },
    vocabAssessment: {
      // Page header
      pageTitle: "How good are you at learning vocabulary?",
      pageDescription: "A quick self-assessment to discover your vocabulary learning strengths and areas to grow.",
      courseTitle: "Vocabulary Assessment",
      backToResources: "Back to Resources",
      // Steps
      stepIntroTitle: "Before You Begin",
      stepIntroDesc: "A quick guide to your assessment",
      stepReflectTitle: "Reflect",
      stepReflectDesc: "Share what's on your mind",
      stepRateTitle: "Rate & React",
      stepRateDesc: "Pick what's most true for you",
      stepResultsTitle: "Your Vocabulary Level",
      stepResultsDesc: "Your personalised result",
      // Intro step
      introHeading: "How good are you at learning vocabulary?",
      introMeta: "11 questions + 3 reflections · about 4 minutes",
      introP1: "Hey there, quirky learner! Are you ready to test your vocabulary learning skills? Whether you're a complete newbie or a seasoned explorer, this test is for you. And don't worry, it's not a vocabulary quiz.",
      introP2: "In this test, I'll ask you questions about your vocabulary learning methods, review habits, and strategies for remembering new words. After answering all the questions, I'll provide you with personalised recommendations to help you on your vocabulary learning journey.",
      introP3: "So, are you ready to discover your strengths and areas for improvement? Let's get started!",
      introCta: "Let's get started",
      // Open-ended
      reflectIntroHeading: "Take a quiet moment",
      reflectIntroBody: "The next three questions are open-ended reflections.\nJust write what comes naturally — short answers are absolutely fine.\nWe'll use your responses to refine your final score.",
      reflectPlaceholder: "Write freely…",
      reflectAnalysing: "Analysing…",
      reflectAnalysisError: "Analysis failed. Continuing without AI scoring.",
      open1: "When you come across a new word in your target language, what do you usually do with it?",
      open2: "Tell me about a time a new word really stuck with you. What helped it stick?",
      open3: "What feels hardest about building your vocabulary right now?",
      // Likert UI
      answeredOf: "{a} of {t} answered",
      previous: "Previous",
      next: "Next",
      continue: "Continue",
      seeMyResult: "See My Result",
      // Results
      analysingTitle: "Analysing Your Responses",
      analysingBody: "Building your personalised result…",
      levelLabel: "Level {n}",
      videoComingSoon: "Video summary coming soon",
      videoComingSoonHint: "Your personalised video will appear here.",
      viewMyResults: "View My Results",
      generating: "Generating…",
      downloadTips: "Download my tips PDF",
      retakeTest: "Retake test",
      pdfError: "Could not generate PDF.",
      saveError: "We couldn't save your results, but you can still view them.",
      // Tier names
      tier1Name: "The Vocabulary Newbie",
      tier2Name: "The Vocabulary Explorer",
      tier3Name: "The Vocabulary Enthusiast",
      tier4Name: "The Vocabulary Master",
      // Questions and options (keyed by id)
      q1Question: "How do you typically learn new vocabulary words?",
      q1Opt0: "I don't have a specific method",
      q1Opt1: "I use flashcards or spaced repetition software",
      q1Opt2: "I write sentences with new words in context",
      q1Opt3: "I actively use new words in conversation and writing",
      q1Opt4: "I experiment with different methods to see what works best for me",
      q2Question: "How often do you review the vocabulary words you've learned?",
      q2Opt0: "I only review them when I have a test or quiz coming up",
      q2Opt1: "I review them once a month",
      q2Opt2: "I review them once a week",
      q2Opt3: "I review them daily",
      q2Opt4: "I use spaced repetition software to help me review regularly",
      q3Question: "What strategies do you use to remember new vocabulary words?",
      q3Opt0: "I don't really have any strategies",
      q3Opt1: "I create mnemonics to help me remember the words",
      q3Opt2: "I use visual aids to help me remember the words",
      q3Opt3: "I make connections to words I already know",
      q3Opt4: "I experiment with different methods until I find what works best for me",
      q4Question: "How do you incorporate new vocabulary words into your speaking and writing practice?",
      q4Opt0: "I don't really incorporate new words into my practice",
      q4Opt1: "I write sentences with the new words to help me remember them",
      q4Opt2: "I participate in language exchanges to practise using new words in conversation",
      q4Opt3: "I speak with native speakers",
      q4Opt4: "I incorporate words into my daily routine",
      q5Question: "How confident are you in using new vocabulary words in conversation with native speakers?",
      q5Opt0: "Not at all confident",
      q5Opt1: "Somewhat not confident",
      q5Opt2: "Neither confident nor not confident",
      q5Opt3: "Somewhat confident",
      q5Opt4: "Very confident",
      q6Question: "How often do you read in the target language to expand your vocabulary?",
      q6Opt0: "Never",
      q6Opt1: "Rarely",
      q6Opt2: "Occasionally",
      q6Opt3: "A few times a week",
      q6Opt4: "Daily or almost daily",
      q7Question: "How comfortable are you with looking up words you don't know in a dictionary or online?",
      q7Opt0: "Not comfortable at all",
      q7Opt1: "Somewhat comfortable",
      q7Opt2: "Fairly comfortable",
      q7Opt3: "Very comfortable",
      q7Opt4: "Extremely comfortable",
      q8Question: "How do you determine the correct pronunciation of new vocabulary words?",
      q8Opt0: "I don't have a specific method",
      q8Opt1: "I listen to native speakers",
      q8Opt2: "I use pronunciation guides",
      q8Opt3: "I practise speaking the words out loud",
      q8Opt4: "I experiment with different methods to see what works best for me",
      q9Question: "Do you understand the difference between active and passive vocabulary?",
      q9Opt0: "No, I'm not familiar with these terms",
      q9Opt1: "I've heard of them, but I'm not sure what they mean",
      q9Opt2: "I know what they mean, but I struggle to activate my passive vocabulary",
      q9Opt3: "I understand the difference and actively work on activating my passive vocabulary",
      q10Question: "Do you make an effort to learn vocabulary words in context?",
      q10Opt0: "No, I just memorise them in isolation",
      q10Opt1: "I try to, but it's difficult",
      q10Opt2: "Yes, I always learn new words in context",
      q10Opt3: "Not only do I learn new words in context, but I also try to use them in my own sentences",
      q10Opt4: "I actively seek out authentic materials to learn new vocabulary in context, such as books, movies, or podcasts",
      q11Question: "How do you assess your vocabulary learning progress?",
      q11Opt0: "I don't really assess my progress",
      q11Opt1: "I try to keep track of new words I learn, but I don't test myself",
      q11Opt2: "I test myself occasionally to see how many words I remember",
      q11Opt3: "I regularly take vocabulary quizzes to track my progress",
      q11Opt4: "I have a detailed system for tracking and categorising new words I learn, and I frequently review my progress",
    },
    myLessons: {
      back: "Dashboard",
      eyebrow: "1-1 with Nahéma",
      title: "My Lessons",
      intro: "Click on a lesson to see the video, summary, transcript and your tasks — all in one place.",
      loading: "Loading…",
      empty: "Your lessons will appear here as soon as the next sync picks up the recordings from your Drive folder.",
      videoBadge: "Video",
      noVideo: "No video found for this lesson.",
      fallbackTitle: "Lesson",
      tabTranscript: "Transcript",
      tabSummary: "Summary",
      tabAskQuestion: "Ask a question",
      tabAskQuestionShort: "Question",
      tabTasks: "Tasks",
      summaryHeader: "Summary",
      detailsHeader: "Details",
      summaryUnavailable: "Summary unavailable.",
      transcriptUnavailable: "Transcript unavailable.",
      translating: "Translating…",
      chatIntro: "Ask your question about this lesson — the AI has access to the full transcript.",
      chatPlaceholder: "Your question…",
      chatThinking: "Thinking…",
      chatErrorPrefix: "Sorry, an error occurred:",
      example1: "What are the main corrections you made for me during this lesson?",
      example2: "Can you explain again the grammar rule we covered?",
      example3: "What are the 5 most important words or expressions to remember?",
      example4: "What did I make the most progress on in this lesson?",
      example5: "Which points should I focus on for my next revision?",
      tasksEmpty: "No tasks yet. Add what you want to revisit before your next lesson.",
      addTaskPlaceholder: "Add a task…",
      deleteAria: "Delete",
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      resources: "The Framework",
      threePillars: "Les Trois Piliers",
      dashboard: "Les 3 Piliers",
      profile: "Profil",
      signOut: "Se déconnecter",
      signIn: "Se connecter",
    },
    navDropdown: {
      induction: "Premiers pas",
      confidence: "Confiance",
      phonetics: "La Récré Phonétique",
      vocabulary: "Vocabulaire musclé",
      fluency: "La fluidité pas à pas",
      learnSmarter: "Apprendre autrement",
      coaching: "Coaching",
      mysteryCalls: "Appels Mystère",
    },
    home: {
      title: "Le Coin Étude",
      subtitle: "Qu'aimerais-tu apprendre aujourd'hui ?",
      intro: "Bienvenue dans ton espace d'apprentissage du français personnalisé ! Ici, tu trouveras tout ce dont tu as besoin pour réussir ton parcours en français. Accède à une riche bibliothèque de ressources d'apprentissage que tu peux explorer à ton rythme, ainsi qu'à tous les supports pédagogiques que nous utilisons ensemble en session. Réserve et rejoins des sessions de coaching 1-1 avec moi, accède aux liens de tes Appels Mystère avec les instructions et formulaires d'évaluation—le tout en un seul endroit pratique. Consulte tes Real-World Missions mensuelles, télécharge tes réalisations et reçois des retours personnalisés dans un espace dédié. De plus, reste informé·e des dernières annonces et mises à jour du site directement sur ta page d'accueil. Rendons ton apprentissage du français passionnant et efficace !",
      startTraining: "Commencer l'entraînement",
      categoriesTitle: "Catégories d'apprentissage",
      categoriesSubtitle: "Choisis une catégorie pour accéder à tes supports d'apprentissage",
      ctaTitle: "Prêt·e à continuer l'apprentissage ?",
      ctaDescription: "Toutes tes ressources t'attendent. Commence à explorer et améliore ton français.",
      viewAllResources: "Voir toutes les ressources",
    },
    category: {
      induction: {
        title: "Premiers pas",
        desc: "Commence ton parcours d'apprentissage du français",
      },
      podcast: {
        title: "Quirky French Podcast",
        desc: "Gagne en confiance, affine ta concentration et prends les rênes de ton parcours d'apprentissage du français",
      },
      topics: {
        title: "Vocabulaire musclé",
        desc: "Entraîne ton vocabulaire français avec des exercices et défis thématiques",
      },
      radio: {
        title: "Radio Quirky French",
        desc: "Émissions de radio et contenu audio",
      },
      phonetics: {
        title: "La Récré Phonétique",
        desc: "Maîtrise la prononciation et les sons français par la pratique ludique",
      },
      grammar: {
        title: "Apprendre autrement",
        desc: "Découvre comment apprendre efficacement et acquérir le français avec plus d'aisance et moins de tension",
      },
      confidence: {
        title: "Confiance",
        desc: "Développe ta confiance en français",
      },
    },
    pillars: {
      title: "Les Trois Piliers",
      subtitle: "Trois façons uniques d'accélérer ton apprentissage du français grâce à un coaching personnalisé, une pratique en situation réelle et des défis passionnants.",
      coaching: {
        title: "Coaching 1-1",
        desc: "Un accompagnement personnalisé pour t'aider à surmonter les défis et atteindre tes objectifs",
        text: "Travaille directement avec moi en séances individuelles pour développer tes compétences en français, surmonter les blocages mentaux et créer une stratégie d'apprentissage personnalisée.",
        bullet1: "1h30 de coaching en direct avec moi en 1-1 pour développer tes compétences globalement et travailler vers ton objectif",
        bullet2: "Retours personnalisés et plans d'action",
        bullet3: "Surmonte ta peur et d'autres blocages mentaux",
        bullet4: "Sors de ta zone de confort et grandis !",
        button: "En savoir plus sur le coaching",
        joinMeeting: "Rejoindre la session de coaching",
        noLinkAvailable: "Le lien de ta session de coaching apparaîtra ici une fois configuré.",
      },
      mystery: {
        title: "Les Appels Mystère",
        desc: "Des conversations téléphoniques surprises pour pratiquer ton français en temps réel",
        text: "Reçois des appels inattendus où tu participeras à de vraies conversations en français. Pas de préparation, juste de la pratique réelle !",
        bullet1: "Pratique orale spontanée",
        bullet2: "Développe ta confiance dans les vraies conversations",
        bullet3: "Surmonte ta peur de parler",
        bullet4: "Reçois des retours immédiats",
        button: "En savoir plus sur les Appels Mystère",
        joinCall: "Rejoindre l'Appel Mystère",
        noLinkAvailable: "Le lien de ton Appel Mystère apparaîtra ici une fois configuré.",
      },
      missions: {
        title: "Real-World Missions",
        desc: "Des défis pratiques pour appliquer ton français dans des situations quotidiennes",
        text: "Réalise des missions passionnantes qui te poussent à utiliser ton français dans des scénarios authentiques de la vie réelle et à développer des compétences de communication pratiques.",
        bullet1: "\"Core Confidence Mission\" sur mesure pour te mettre au défi dans la vraie vie",
        bullet2: "Instructions vidéo et manuel personnalisé",
        bullet3: "Séance de retour dédiée après avoir terminé la tâche",
        bullet4: "Prouve-toi de quoi tu es capable !",
        button: "En savoir plus sur les Missions",
      },
      cta: {
        title: "Prêt·e à faire passer ton français au niveau supérieur ?",
        description: "Ces trois piliers fonctionnent ensemble pour fournir un soutien complet à ton parcours d'apprentissage du français. Choisis le chemin qui résonne avec toi, ou combine-les pour un impact maximal !",
        button: "Explorer toutes les ressources",
      },
    },
    resources: {
      title: "Ressources d'apprentissage",
      subtitle: "Accède à tous tes supports d'apprentissage du français organisés par catégorie",
      loading: "Chargement des ressources...",
      noResources: "Aucune ressource disponible dans cette catégorie pour le moment.",
      backToCategories: "Retour aux catégories",
      podcast: {
        title: "Écoute le Quirky French Podcast",
      },
      phonetics: {
        title: "Cours de Phonétique",
        description: "Bienvenue à La Récré Phonétique ! Tu trouveras ici des activités d'entraînement auditif et des exercices de prononciation conçus pour t'aider à maîtriser les sons français.",
        button: "Soumettre tes enregistrements",
        introTitle: "Introduction à la Phonétique",
        accentChallenge: "Accent Challenge",
        accentChallengeDesc: "Maîtrise la prononciation française avec des exercices guidés et des retours personnalisés",
        phoneticsCorrection: "Correction Phonétique",
        phoneticsCorrectionDesc: "Reçois des corrections détaillées sur ta prononciation",
        frenchFlow: "French Flow",
        frenchFlowDesc: "Développe un rythme et une fluidité naturels en français",
        syllableWonderland: "Le Pays des Syllabes",
        syllableWonderlandDesc: "Pratique les patterns et la structure des syllabes françaises",
        voiceoverBooth: "Le Studio de Voix-Off",
        voiceoverBoothDesc: "Pratique l'expression orale professionnelle en français",
        comingSoon: "Bientôt disponible",
      },
      grammar: {
        title: "Verb Echo",
        description: "Maîtrise les conjugaisons verbales françaises avec notre entraîneur interactif. Pratique différents temps et modes en modes structuré et aléatoire pour développer ta confiance.",
        button: "Commencer à pratiquer",
      },
      vocabularyGym: "Vocabulaire musclé",
      vocabularyGymDesc: "Enrichis ton vocabulaire, ancre-le dans ta mémoire et active tes connaissances passives",
      pathToFluency: "La fluidité pas à pas",
      pathToFluencyDesc: "Apprends à lever les barrières entre tes pensées et ta parole — des émotions à la prononciation — et communique avec plus d'aisance",
      comingSoon: "Bientôt disponible",
      videoPlaceholder: "Espace vidéo",
      grammarCourses: "Cours de Grammaire",
      grammarIntro: "Maîtrise la grammaire française avec nos cours complets conçus pour développer ta confiance et ta précision.",
      conjugationTrainer: "Verb Echo",
      conjugationTrainerDesc: "Entraîne tes conjugaisons par l'écoute et la parole",
      grammarFundamentals: "Fondamentaux de la grammaire",
      grammarFundamentalsDesc: "Règles et structures grammaticales essentielles",
      advancedGrammar: "Grammaire avancée",
      advancedGrammarDesc: "Structures complexes et usage nuancé",
      fluencyTools: "Outils de Fluidité",
      fluencyIntro: "La fluidité, c'est réduire la friction entre tes pensées et ta parole. Dans ce cours, tu n'apprendras pas plus de français — tu développeras les compétences essentielles pour lever les barrières qui te ralentissent : tes émotions, tes pensées, ton vocabulaire, ta grammaire et ta prononciation. Avance à ton rythme, concentre-toi sur une barrière à la fois, et observe ta communication se transformer.",
      aiConversationPractice: "Pratique de conversation IA",
      aiConversationDesc: "Pratique de vraies conversations avec l'IA en utilisant ta voix",
      tryNow: "Essayer maintenant",
      fluencyChallenges: "Défis de Fluidité",
      fluencyChallengesDesc: "Défis quotidiens pour améliorer ta fluidité",
      expressionBuilder: "Constructeur d'Expressions",
      expressionBuilderDesc: "Apprends et pratique des expressions françaises naturelles",
      listening: {
        title: "Écoute HD",
        description: "Entraîne ton oreille à vraiment entendre le français — affine tes instincts d'écoute et développe les compétences qui te maintiennent dans le flux des vraies conversations.",
      },
      induction: {
        welcome: "Bienvenue sur Le Coin Étude.",
        skillOfLearning: "Bienvenue sur Le Coin Étude",
        skillOfLearningDesc: "Comment apprenons-nous ce que nous apprenons ?",
        fourKeyConcepts: "Quatre concepts clés",
        fourKeyConceptsDesc: "Découvre quatre concepts clés qui vont booster ton apprentissage.",
        mythAboutTime: "Le mythe du temps",
        mythAboutTimeDesc: "Trouve ton rythme idéal et ta routine de pratique.",
        engagement: "Engagement",
        engagementDesc: "Il ne s'agit pas d'apprendre vite ou d'apprendre beaucoup. Il s'agit de s'engager à progresser.",
        // Module 2 checklist
        m2WatchVideo: "Regarde la vidéo sur la compétence d'apprendre",
        m2IdentifyStyle: "Identifie ton style d'apprentissage personnel",
        m2ReflectLearning: "Réfléchis à comment tu as appris des langues ou des compétences auparavant",
        // Module 2 questions
        m2DefineLearning: "Avec tes propres mots, définis ce que signifie apprendre pour toi.",
        m2PastSkill: "Pense à une compétence que tu as apprise dans le passé, qu'est-ce qu'il t'a fallu pour passer de débutant·e à compétent·e ? Qu'as-tu dû faire pour acquérir cette compétence ?",
        m2Strategies: "Voici quelques exemples de stratégies d'apprentissage pour apprendre le français : apprendre de nouveaux mots en les cherchant dans un dictionnaire, regarder des films en français, apprendre des chansons françaises, utiliser des flashcards... Liste les stratégies que tu as utilisées dans ton apprentissage. Note-en autant que possible.",
        m2StrategiesImportance: "Quelle importance accordes-tu au développement de stratégies d'apprentissage efficaces ?",
        // Module 3 checklist
        m3WatchVideo: "Regarde la vidéo sur les quatre concepts clés",
        m3UnderstandConcepts: "Prends des notes sur chacun des quatre concepts clés",
        m3ApplyConcepts: "Réfléchis à comment appliquer ces concepts à ton apprentissage du français",
        // Module 3 questions
        m3GoalSetting: "Définition d'objectif - Décris ta raison d'apprendre le français.",
        m3UltimateGoal: "Quel est ton objectif ultime ? Sois aussi clair·e que possible. Par exemple « Je veux parler français » est trop vague. Bien que « Je veux parler français couramment » soit mieux, c'est encore trop flou car la fluidité est un concept très complexe qui peut signifier beaucoup de choses. En revanche « D'ici un an, je veux atteindre un niveau tel que je puisse voyager en France en vacances et me sentir confiant·e pour parler sans jamais avoir à passer à l'anglais » est un objectif bien plus clair.",
        m3ConstraintList: "Contrainte - Fais un brainstorming et liste les choses les plus importantes que tu souhaites améliorer en français.",
        m3ConstraintPriority: "Regarde maintenant chaque élément listé. Si tu ne pouvais en améliorer qu'un seul, lequel choisirais-tu ? Est-ce que cela te rapprocherait de ton objectif ultime ? Oui : passe à la question 5. Non : regarde la liste et choisis l'élément le plus important qui te rapprocherait de ton objectif avant de passer à la question 5.",
        m3TopPriorities: "Répète maintenant le processus en regardant les éléments restants et choisis la prochaine chose la plus importante sur laquelle tu travaillerais si tu ne pouvais travailler que sur un domaine. Continue le processus de la question 4 jusqu'à pouvoir lister tes 5 priorités principales. Note-les ici :\n1.\n2.\n3.\n4.\n5.",
        m3Courage: "Courage - Comment comptes-tu gérer les revers et les difficultés pour étudier, te concentrer, rester motivé·e, etc. ? Fais un brainstorming d'idées pour te préparer aux « mauvais jours » afin de rester engagé·e dans le processus d'apprentissage.",
        m3Consistency: "Régularité - La régularité est très importante dans le processus d'apprentissage des langues. L'essentiel est d'apprendre peu mais souvent, et l'intensité de ton travail ne se mesure pas au nombre d'heures accumulées. As-tu été régulier·ère dans ton apprentissage jusqu'ici ? Quels comportements irréguliers devrais-tu éliminer de ta routine et quels comportements réguliers peux-tu mettre en place dès maintenant ? Note quelques idées ici.",
        // Module 4 checklist
        m4WatchVideo: "Regarde la vidéo sur le mythe du temps",
        m4CalculateTime: "Calcule ton temps de pratique disponible par semaine",
        m4CreateSchedule: "Crée un planning de pratique du français réaliste",
        // Module 4 questions
        m4StudyApproach: "Quelle a été ton approche pour étudier jusqu'à présent ? As-tu une routine horaire particulière ?",
        m4TaskOriented: "De l'approche orientée temps à l'approche orientée tâche. Décris à quoi ressemblerait maintenant l'étude si tu avais une approche orientée tâche dans ton apprentissage ?",
        m4ComplexTasks: "Comment aborderais-tu des tâches d'étude vastes et complexes qui pourraient prendre plusieurs heures à accomplir ?",
        // Module 5 checklist
        m5WatchVideo: "Regarde la vidéo sur l'engagement et l'implication",
        m5Commitment: "Prends un engagement personnel envers ton parcours d'apprentissage du français",
        m5ShareGoal: "Note ton plan de responsabilisation",
        // Module 5 questions
        m5Takeaway: "Que retiens-tu des vidéos d'induction jusqu'ici ?",
        m5Prepared: "Te sens-tu prêt·e à prendre en charge ton apprentissage ?",
        m5Observations: "Quelles observations et questions as-tu pour moi jusqu'à présent ? Note-les ici et contacte-moi pour partager et discuter de n'importe quel point si tu le souhaites.",
      },
      blueprint: "The Blueprint",
      blueprintDesc: "Maîtrise la syntaxe et la structure des phrases françaises",
      verbEcho: "Verb Echo",
      verbEchoDesc: "Booste tes verbes par l'écoute et la parole",
      fluency: {
        courseIntro: "Introduction du cours",
        courseIntroDesc: "Ce que la fluidité signifie vraiment et comment fonctionne ce cours",
        emotions: "1. Tes émotions te ralentissent",
        emotionsDesc: "Surmonter la peur de parler",
        thoughts: "2. Tes pensées te ralentissent",
        thoughtsDesc: "L'activité des 3 points clés",
        vocabulary: "3. Ton vocabulaire te ralentit",
        vocabularyDesc: "Développer les compétences de circumlocution",
        grammar: "4. Ta grammaire te ralentit",
        grammarDesc: "Construire la fluidité grammaticale",
        body: "5. Ton corps te ralentit",
        bodyDesc: "Prononciation et fluidité physique",
        aiPractice: "Pratique de conversation IA",
        aiPracticeDesc: "Pratique de vraies conversations avec un tuteur IA",
      },
      confidence: {
        coursesTitle: "Cours de Confiance",
        diary: "Journal de Confiance Quirky French",
        diaryDesc: "Un journal guidé de 4 semaines pour vaincre la peur de parler, développer ta résilience et transformer ta relation avec le français — un pas décalé à la fois.",
        stayingConnected: "Rester connecté·e au français",
        stayingConnectedDesc: "Un compagnon d'écoute réflexif pour t'aider à faire une pause, te reconnecter et rester proche du français — sans pression, à ton rythme.",
        playlist: "Playlist Confiance",
        playlistDesc: "Collection d'épisodes du Quirky French Podcast pour booster ta confiance et dynamiser ton parcours en français.",
      },
      vocabularyCourses: "Cours de Vocabulaire",
      vocabularyIntro: "Élargis ton vocabulaire français avec des cours thématiques et des exercices pratiques conçus pour t'aider à parler plus naturellement.",
      startHere: "Commence ici : à quel point es-tu bon·ne pour apprendre du vocabulaire ?",
      startHereDesc: "Évalue tes compétences d'apprentissage du vocabulaire et découvre tes forces",
      topicsOfMonth: "Thèmes du Mois",
      topicsOfMonthDesc: "Leçons et exercices de vocabulaire thématiques mensuels",
      vocabularyPlaylist: "Playlist de Vocabulaire",
      vocabularyPlaylistDesc: "Collection organisée de ressources et de matériels de vocabulaire",
      passiveToActive: "Du Vocabulaire Passif au Vocabulaire Actif",
      passiveToActiveDesc: "Transforme les mots que tu comprends en mots que tu utilises",
      backToVocabCourses: "Retour aux Cours de Vocabulaire",
      topicsOfMonthDetail: "Leçons et exercices de vocabulaire thématiques mensuels pour élargir ton vocabulaire français",
      topicsCheckLevel: "Teste ton niveau",
      topicsOpenMaterials: "Ouvrir les ressources",
      pronunciationRecorder: "Mon Labo Vocal",
      pronunciationRecorderDesc: "Ton espace perso pour enregistrer, expérimenter et recevoir des retours détaillés sur tout ce que tu veux dire en français",
    },
    resourceCard: {
      accessResource: "Accéder à la ressource",
      copyLink: "Copier le lien",
      linkCopied: "Lien copié",
      linkCopiedDesc: "Colle-le dans ton navigateur si Google Drive est bloqué.",
      couldNotCopy: "Impossible de copier",
      couldNotCopyDesc: "Copie le lien manuellement s'il te plaît.",
      driveNote: "Si Google Drive est bloqué sur ton réseau, copie le lien et ouvre-le dans un autre navigateur/appareil.",
      audio: "Audio",
      video: "Vidéo",
      pdf: "PDF",
      document: "Document",
    },
    auth: {
      welcome: "Bienvenue sur Le Coin Étude",
      subtitle: "Connecte-toi ou crée un compte pour commencer à apprendre",
      signIn: "Se connecter",
      signUp: "S'inscrire",
      email: "Email",
      password: "Mot de passe",
      fullName: "Nom complet",
      firstName: "Prénom",
      lastName: "Nom de famille",
      signingIn: "Connexion en cours...",
      creatingAccount: "Création du compte...",
      createAccount: "Créer un compte",
      forgotPassword: "Mot de passe oublié ?",
      resetPassword: "Réinitialiser le mot de passe",
      resetPasswordDesc: "Entre ton email et nous t'enverrons un lien pour réinitialiser ton mot de passe.",
      sendResetLink: "Envoyer le lien",
      sending: "Envoi en cours...",
      resetLinkSent: "Lien envoyé ! Vérifie tes emails.",
      newPassword: "Nouveau mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      updatePassword: "Mettre à jour le mot de passe",
      updating: "Mise à jour...",
      passwordUpdated: "Mot de passe mis à jour avec succès !",
      passwordMismatch: "Les mots de passe ne correspondent pas.",
      backToSignIn: "Retour à la connexion",
    },
    profile: {
      loading: "Chargement...",
      editProfile: "Modifier le Profil",
      cancel: "Annuler",
      firstName: "Prénom",
      surname: "Nom",
      phoneNumber: "Numéro de Téléphone",
      dateOfBirth: "Date de Naissance",
      saveChanges: "Enregistrer",
      currentStreak: "Série Actuelle",
      days: "jours",
      keepGoing: "Continue comme ça !",
      completedSessions: "Sessions Terminées",
      of: "sur",
      total: "total",
      lastSession: "Dernière Session",
      noSessions: "Aucune session",
      mostRecent: "Activité la plus récente",
      progress: "Progrès",
      overallCompletion: "Achèvement global",
      sessionCalendar: "Calendrier des Sessions",
      trackSessions: "Suis tes sessions terminées",
      overallProgress: "Progrès Global",
      sessionsCompleted: "sessions terminées",
      completed: "Terminé",
      remaining: "Restant",
      achievements: "Réalisations",
      learningMilestones: "Tes jalons d'apprentissage",
      weekWarrior: "Guerrier Hebdomadaire",
      weekStreak: "Série de 7 jours atteinte",
      sessionMaster: "Maître des Sessions",
      tenSessions: "10 sessions terminées",
      halfwayHero: "Héros à Mi-Parcours",
      fiftyPercent: "50% de progrès atteint",
      avatarUpdated: "Avatar mis à jour !",
      avatarFailed: "Échec du téléchargement de l'avatar",
      profileUpdated: "Profil mis à jour !",
      profileFailed: "Échec de la mise à jour du profil",
      learner: "Apprenant",
      born: "Né(e) le",
      longestStreak: "Plus longue série",
      personalBest: "Record personnel",
      resourcesFinished: "Ressources terminées",
      overallProgressDesc: "Tu as terminé {completed} ressources sur {total}",
      completedResources: "terminées",
      inProgress: "en cours",
    },
    coaching: {
      title: "Coaching 1-1",
      description: "Ton hub de coaching personnel - sessions, matériel et ressources",
      joinCall: "Rejoindre l'Appel de Coaching",
      joinCallDesc: "Rejoins ta prochaine session de coaching 1-1",
      myWorkspace: "Mon Espace de Travail",
      myWorkspaceDesc: "Consulte ton document d'espace de travail",
      viewRecordings: "Voir les Enregistrements",
      viewRecordingsDesc: "Parcours les enregistrements de tes sessions",
      sessionDocuments: "Documents de Session",
      sessionDocumentsDesc: "Accède aux supports et notes de session",
      comingSoon: "Prochainement",
    },
    mysteryCalls: {
      title: "Appels Mystère",
      description: "Ton hub d'appels mystère - rejoins les appels, complète les formulaires et accède aux enregistrements",
      joinCall: "Rejoindre l'Appel Mystère",
      joinCallDesc: "Rejoins ton appel mystère prévu",
      selfAssessment: "Ton Auto-Évaluation",
      selfAssessmentDesc: "Complète ton formulaire d'auto-évaluation",
      guestFeedback: "Retour d'Invité",
      guestFeedbackDesc: "Les retours de ton invité mystère",
      pastRecordings: "Enregistrements Passés",
      pastRecordingsDesc: "Regarde tes anciens appels mystère",
      scheduleCall: "Mets à jour ton calendrier",
      scheduleCallDesc: "Indique tes disponibilités pour le mois prochain",
      introduction: "Introduction",
      introductionDesc: "Commence avec les appels mystère",
      meetTheTeam: "Rencontrer l'Équipe",
      meetTheTeamDesc: "Fais connaissance avec tes partenaires d'appels mystère",
      comingSoon: "Prochainement",
    },
    monthlyMission: {
      title: "Mission Mensuelle",
      noMission: "Aucune mission assignée pour le moment",
      coachWillAssign: "Ton coach t'assignera bientôt une mission !",
      uploadDocument: "Télécharger un Document",
      pastMissions: "Missions Passées",
      soon: "Bientôt",
      yourUploads: "Tes Téléchargements",
      feedback: "Retour",
      loadingMission: "Chargement de la mission...",
      uploadSuccess: "Fichier téléchargé avec succès !",
      uploadFailed: "Échec du téléchargement du fichier",
    },
    contact: {
      title: "Me Contacter",
      description: "Pose une question ou partage une recommandation",
      selectType: "Sélectionne le type de message",
      question: "Question",
      recommendation: "Recommandation",
      other: "Autre",
      placeholder: "Tape ton message ici...",
      sending: "Envoi...",
      sendMessage: "Envoyer le Message",
      messageSent: "Message envoyé avec succès !",
      messageFailed: "Échec de l'envoi du message",
      enterMessage: "Veuillez entrer un message",
    },
    personalNotes: {
      title: "Mes Notes Personnelles",
      description: "Écris des notes pour toi-même",
      placeholder: "Écris tes notes personnelles ici...",
      saving: "Enregistrement...",
      saveNote: "Enregistrer la Note",
      noteSaved: "Note enregistrée !",
      noteFailed: "Échec de l'enregistrement de la note",
      enterNote: "Veuillez entrer une note",
      loadingNotes: "Chargement des notes...",
    },
    accent: {
      title: "Accent Challenge",
      subtitle: "Télécharge tes enregistrements de prononciation pour recevoir des retours et suivre ta progression.",
      backToResources: "Retour aux ressources",
      submitRecording: "Soumettre un enregistrement",
      selectModule: "Sélectionner un module",
      chooseModule: "Choisis un module",
      audioFile: "Fichier audio",
      audioFileDesc: "Télécharge ton enregistrement de prononciation (MP3, WAV, M4A)",
      notes: "Notes (facultatif)",
      notesPlaceholder: "Ajoute des notes sur ton enregistrement...",
      submit: "Soumettre l'enregistrement",
      submitting: "Envoi en cours...",
      yourRecordings: "Tes enregistrements",
      noRecordings: "Aucun enregistrement pour le moment. Télécharge ton premier enregistrement pour recevoir des retours !",
      loading: "Chargement de tes enregistrements...",
      delete: "Supprimer",
      deleting: "Suppression...",
    },
    accentCourse: {
      pageTitle: "Accent Challenge",
      pageDescription: "Booste ton accent français en 6 semaines avec des exercices quotidiens",
      backToAccentChallenge: "Retour à l'Accent Challenge",
      yourProgress: "Ta progression",
      navCourseOverview: "Présentation du cours",
      navCourseOverviewDesc: "Découvre en quoi consiste l'Accent Challenge",
      navInitialTest: "Test initial",
      navInitialTestDesc: "Identifie tes forces et tes faiblesses en prononciation",
      navWeek: "Semaine",
      slideIntroTitle: "Introduction",
      slideIntroDesc: "Qu'est-ce que l'Accent Challenge ?",
      slideHowItWorksTitle: "Comment ça marche",
      slideHowItWorksDesc: "Comment compléter ton challenge",
      slideHowItWorksStep1: "Passe un test initial pour repérer tes points faibles.",
      slideHowItWorksStep2: "Chaque semaine, accède à un challenge à compléter.",
      slideHowItWorksStep3: "Des leçons et des exercices t'aident à t'entraîner d'abord.",
      slideHowItWorksStep4: "Utilise l'outil d'enregistrement pour soumettre et recevoir des retours.",
      slideHowItWorksHint: "Termine le test initial pour débloquer la semaine 1.",
      slideDocumentsTitle: "Documents du cours",
      slideDocumentsDesc: "Télécharge les documents pour suivre ta progression",
      slideDocumentsCourseDetails: "Télécharger les détails du cours",
      slideDocumentsDiary: "Télécharger le journal de l'Accent Challenge",
      slideDocumentsGoToTest: "Aller au test initial",
      previous: "Précédent",
      next: "Suivant",
      initialTestDone: "Test initial terminé ! La semaine 1 est maintenant débloquée.",
      goToWeek1: "Aller à la semaine 1 : Vowel Victory",
      submitDayRecordingToContinue: "Soumets ton enregistrement du jour {n} pour continuer",
      submitMissionRecordingToContinue: "Soumets l'enregistrement de la mission pour continuer",
      unlocksAt: "Se débloque à {time}",
      completeTodayToUnlockNextDay: "Termine l'enregistrement du jour pour débloquer le suivant.",
      completeTodayToUnlockMission: "Termine l'enregistrement du jour pour débloquer la mission.",
      completePrevWeekMission: "Termine la mission de la semaine précédente pour débloquer",
      readyToUnlock: "Prêt à débloquer !",
      day: "Jour",
      missionPreviewTitle: "Découvre la mission — Semaine {n}",
      missionPreviewDesc: "Prends un moment pour parcourir le document de mission. C'est juste un aperçu pour savoir ce qui t'attend cette semaine. Tu compléteras et enregistreras la mission au jour 7, après avoir terminé toutes les leçons quotidiennes.",
      openMissionDoc: "Ouvrir le document de mission",
      lessonOpenHint: "Ouvre la leçon, puis pratique les exercices avant de continuer.",
      openLessonOfDay: "Ouvrir la leçon du jour",
      tipOfTheDay: "Astuce du jour",
      tipOfTheDayAlt: "Astuce du jour {n}",
      recordPracticeHint: "Enregistre ta pratique du jour {n} et soumets-la pour recevoir des retours.",
      completeMissionTitle: "Compléter la mission — Semaine {n}",
      completeMissionDesc: "Tu as terminé les 6 jours de pratique ! Relis le document de mission, puis enregistre ta soumission.",
      recordMissionHint: "Enregistre et soumets ta mission ci-dessous.",
      weekCompleteTitle: "Semaine {n} terminée !",
      weekCompleteFinalDesc: "Félicitations ! Tu as terminé tout l'Accent Challenge. Bravo !",
      weekCompleteDesc: "Bravo pour avoir terminé la mission de cette semaine ! Ton coach va relire ton enregistrement.",
      goToWeekN: "Aller à la semaine {n}",
      weekNUnlocksSoon: "Semaine {n} — bientôt débloquée",
      recordingsTitle: "Tes enregistrements",
      recordingsDescription: "Enregistre ou télécharge tes exercices de prononciation",
      recordingTitlePlaceholder: "Titre de l'enregistrement (facultatif)",
      record: "Enregistrer",
      stopRecording: "Arrêter l'enregistrement",
      uploadFile: "Télécharger un fichier",
      uploading: "Téléchargement...",
      yourRecordingsLabel: "Tes enregistrements :",
      rename: "Renommer",
      download: "Télécharger",
      defaultRecordingName: "Enregistrement {n}",
      toastError: "Erreur",
      toastCannotLoadRecordings: "Impossible de charger les enregistrements",
      toastRecordingSaved: "Enregistrement sauvegardé",
      toastRecordingSavedDesc: "Ton enregistrement a été sauvegardé avec succès",
      toastCannotSaveRecording: "Impossible de sauvegarder l'enregistrement",
      toastRecordingInProgress: "Enregistrement en cours",
      toastSpeakNow: "Parle maintenant...",
      toastCannotAccessMic: "Impossible d'accéder au microphone",
      toastRecordingAdded: "Enregistrement ajouté",
      toastRecordingAddedDesc: "Ton fichier audio a été ajouté avec succès",
      toastCannotUploadFile: "Impossible de télécharger le fichier",
      toastRecordingDeleted: "Enregistrement supprimé",
      toastCannotDeleteRecording: "Impossible de supprimer l'enregistrement",
      toastCannotDownloadRecording: "Impossible de télécharger l'enregistrement",
      toastTitleUpdated: "Titre modifié",
      toastTitleUpdatedDesc: "Le titre de ton enregistrement a été modifié avec succès",
      toastCannotUpdateTitle: "Impossible de modifier le titre",
      renameDialogTitle: "Renommer l'enregistrement",
      renameDialogDesc: "Modifier le titre de ton enregistrement",
      newTitle: "Nouveau titre",
      cancel: "Annuler",
      save: "Enregistrer",
      coachFeedback: "Feedback du coach",
      statusPending: "En attente",
      statusReviewed: "Révisé",
      statusNeedsRevision: "À réviser",
      lastUpdated: "Dernière mise à jour : {date}",
      noFeedbackYet: "Aucun feedback pour le moment",
      status: "Statut",
      pronunciationComments: "Commentaires de prononciation",
      feedbackPlaceholder: "Entre tes commentaires sur la prononciation, les points à améliorer et les réussites de l'apprenant...",
      feedbackTip: "💡 Conseil : Mentionne des timestamps spécifiques (ex : « À 0:15, le son 'u'... ») pour des commentaires plus précis",
      sendFeedback: "Envoyer le feedback",
      saving: "Sauvegarde...",
      enterAComment: "Veuillez entrer un commentaire",
      feedbackSaved: "Feedback sauvegardé",
      feedbackSavedDesc: "Ton commentaire a été enregistré avec succès",
      cannotSaveFeedback: "Impossible de sauvegarder le feedback",
      feedbackPendingMessage: "Ton enregistrement est en attente de relecture par ton coach.",
      yourCoachFeedback: "Feedback de ton coach",
      receivedOn: "Reçu le {date}",
    },
    conjugation: {
      title: "Verb Echo",
      backToResources: "Retour aux ressources",
      configuration: "CONFIGURATION",
      description: "Maîtrise les conjugaisons verbales françaises avec notre entraîneur interactif proposant les 250 verbes français les plus courants. Pratique différents temps et modes en modes structuré et aléatoire pour développer ta confiance et ta fluidité.",
      selectVerbs: "Choisis ton verbe",
      searchVerbs: "Rechercher des verbes...",
      selectTenses: "Choisis ton temps",
      continue: "Continuer",
      modeSelection: "MODE D'ENTRAÎNEMENT",
      chooseMode: "Choisis un mode",
      structuredMode: "Pratique structurée",
      structuredDesc: "Pratique un verbe à la fois, en parcourant tous les temps sélectionnés de manière systématique.",
      randomMode: "Défi aléatoire",
      randomDesc: "Obtiens des combinaisons aléatoires de verbes et de temps pour une pratique variée.",
      startTraining: "Commencer l'entraînement",
      specialChars: "Caractères spéciaux :",
      checkAnswers: "Vérifier les réponses",
      nextExercise: "Exercice suivant",
      finishTraining: "Terminer l'entraînement",
      results: "Résultats",
      finalScore: "Ton score final :",
      startOver: "Recommencer",
      loginRequired: "Connexion requise",
      flashcardSaved: "Flashcard sauvegardée !",
      flashcardSavedDesc: "Ce verbe a été ajouté à ton jeu de révision",
      errorSaving: "Erreur lors de la sauvegarde",
      saveFlashcard: "Sauvegarder en flashcard",
      reviewFlashcards: "Réviser les flashcards",
      focusMode: "Pratique ciblée sur tes temps les plus faibles",
      focusingOn: "Concentration sur",
      conjugateVerb: "Conjugue ce verbe",
      fillConjugation: "Complète la conjugaison correcte",
      verb: "Verbe",
      tense: "Temps",
      pronoun: "Pronom",
      typeAnswer: "Tape ta réponse...",
      enterAnswer: "Veuillez entrer une réponse",
      correct: "Correct !",
      incorrect: "Incorrect.",
      checkAnswer: "Vérifier la Réponse",
      score: "Score",
      subtitle: "Pratique les conjugaisons verbales françaises",
      correctAnswer: "Réponse correcte",
      viewStats: "Voir les stats",
    },
    flashcards: {
      loading: "Chargement des flashcards...",
      noDueCards: "Aucune carte à réviser",
      noDueCardsDesc: "Excellent travail ! Toutes tes flashcards sont à jour. Pratique davantage dans l'entraîneur de conjugaison pour ajouter de nouvelles cartes.",
      back: "Retour à l'entraîneur",
      showAnswer: "Montrer la réponse",
      ratePerformance: "À quel point connaissais-tu cela ?",
      tryRecall: "Essaie de te souvenir de toutes les conjugaisons avant de montrer la réponse",
      sessionComplete: "Session de révision terminée !",
      correct: "Correct",
      accuracy: "Précision",
      errorLoading: "Erreur lors du chargement",
      errorUpdating: "Erreur lors de la mise à jour",
      errorAudio: "Erreur de lecture audio",
    },
    stats: {
      title: "Statistiques de Pratique",
      subtitle: "Suis tes progrès et identifie les domaines à améliorer",
      dashboardIntro: "Maîtrise les conjugaisons françaises avec une pratique ciblée. Commence une nouvelle session ou consulte tes progrès ci-dessous.",
      goToConfiguration: "Commencer une nouvelle session",
      loading: "Chargement des statistiques...",
      backToTrainer: "Retour à l'entraîneur",
      backToResources: "Retour aux ressources",
      backToDashboard: "Retour aux 3 Piliers",
      startTraining: "Pratiquer tous les temps",
      focusWeakTenses: "Se concentrer sur les temps faibles",
      focusWeakTensesDesc: "Pratiquer les temps avec lesquels vous avez le plus de difficultés",
      practiceAll: "Pratiquer tous les temps",
      practiceAllDesc: "Pratique aléatoire de tous les temps verbaux",
      startFocused: "Commencer la pratique ciblée",
      totalAttempts: "Total d'Essais",
      conjugationsPracticed: "conjugaisons pratiquées",
      overallAccuracy: "Précision Globale",
      tensesPracticed: "Temps Pratiqués",
      differentTenses: "temps différents",
      recentActivity: "Activité Récente",
      active: "Actif",
      inactive: "Inactif",
      last30Days: "30 derniers jours",
      byTense: "Par Temps",
      progress: "Progrès",
      byVerb: "Par Verbe",
      tenseDifficulty: "Difficulté par Temps",
      tenseDifficultyDesc: "Vois quels temps tu trouves les plus difficiles",
      mostChallenging: "Temps les Plus Difficiles",
      mostChallengingDesc: "Concentre-toi sur ceux-ci pour t'améliorer",
      accuracyOverTime: "Précision dans le Temps",
      accuracyOverTimeDesc: "Suis tes progrès sur les 30 derniers jours",
      topVerbs: "Verbes Maîtrisés",
      topVerbsDesc: "Verbes que tu maîtrises",
      strugglingVerbs: "À Pratiquer",
      strugglingVerbsDesc: "Concentre-toi sur ces verbes",
      attempts: "essais",
      correct: "Correct",
      incorrect: "Incorrect",
      accuracy: "Précision",
      noDataYet: "Pas encore de données. Commence à t'entraîner pour voir tes stats !",
      keepPracticing: "Continue à Pratiquer !",
      keepPracticingDesc: "Plus tu pratiques, meilleures seront tes statistiques",
      errorLoading: "Erreur lors du chargement des statistiques",
    },
    notFound: {
      title: "404",
      message: "Oups ! Page introuvable",
      returnHome: "Retour à l'accueil",
    },
    aiConversation: {
      title: "Pratique de conversation IA",
      subtitle: "Pratique ton français en parlant avec l'IA. Clique sur le micro pour commencer !",
      backToResources: "Retour aux ressources",
      version1Notice: "Ceci est la version 1.0 avec synthèse vocale basique. Des voix améliorées et plus naturelles seront bientôt disponibles ! N'hésite pas à commencer à pratiquer maintenant - tes compétences de conversation se transféreront parfaitement à la version améliorée.",
      scenarioLabel: "Scénario",
      difficultyLabel: "Niveau de difficulté",
      generalConversation: "Conversation générale",
      restaurant: "Restaurant",
      travel: "Voyage",
      shopping: "Shopping",
      beginner: "Débutant",
      intermediate: "Intermédiaire",
      advanced: "Avancé",
      clickToStart: "Clique sur le micro ci-dessous pour commencer ta conversation en français",
      you: "Toi",
      aiTutor: "Tuteur IA",
      thinking: "Réflexion...",
      stopListening: "Arrêter d'écouter",
      startSpeaking: "Commencer à parler",
      stopSpeaking: "Arrêter de parler",
      newConversation: "Nouvelle conversation",
      listening: "Écoute en cours... Parle en français",
      tipsTitle: "Conseils pour une meilleure conversation :",
      tip1: "Parle clairement et à un rythme normal",
      tip2: "Utilise Chrome ou Edge pour de meilleurs résultats",
      tip3: "Assure-toi que ton micro est activé",
      tip4: "L'IA répondra en français à ton niveau choisi",
      tip5: "Sélectionne différents scénarios pour pratiquer des situations spécifiques",
      history: "Historique",
      analytics: "Analyses",
      newConversationStarted: "Nouvelle session ! Clique sur le micro pour commencer.",
      sessionLoaded: "Conversation précédente chargée avec succès.",
    },
    conversationHistory: {
      title: "Conversations récentes",
      loading: "Chargement de l'historique...",
      noHistory: "Pas encore d'historique de conversation. Commence une conversation pour la voir ici !",
      messages: "messages",
      load: "Charger",
      deleted: "Session de conversation supprimée",
      deleteError: "Échec de la suppression de la session",
    },
    conversationAnalytics: {
      loading: "Chargement des analyses...",
      noData: "Pas encore de données de conversation. Commence à pratiquer pour voir tes analyses !",
      conversations: "Conversations",
      speakingTime: "Temps de parole",
      totalMessages: "Messages totaux",
      avgMessages: "Messages moy.",
      min: "min",
      mostPracticed: "Scénarios les plus pratiqués",
      activityLast30Days: "Activité sur les 30 derniers jours",
      practiceByDifficulty: "Pratique par niveau de difficulté",
      general: "Général",
      beginner: "Débutant",
      intermediate: "Intermédiaire",
      advanced: "Avancé",
    },
    dashboard: {
      welcome: "Salut",
      ready: "Prêt·e pour ta prochaine session ?",
      loading: "Chargement...",
    },
    index: {
      welcomeTitle: "Bienvenue sur Le Coin Étude !",
      welcomeIntro1: "Ici, tu trouveras tout ce dont tu as besoin pour réussir ton parcours Quirky French. Accède à une riche bibliothèque de ressources d'apprentissage que tu peux explorer à ton rythme, ainsi qu'à tous les supports pédagogiques que nous utilisons ensemble en session.",
      welcomeIntro2: "De plus, retrouve tout ce dont tu as besoin dans tes 3 Piliers : tes liens vers nos sessions 1-1, les liens et fichiers d'Appels Mystère, tout est maintenant dans un seul endroit pratique ! Mais il y a plus, alors explore un peu et n'hésite pas à m'envoyer un message si tu as une question, un commentaire ou une suggestion!",
      welcomeIntro3: "On y va ! Allons-y !",
      latestUpdates: "Dernières Mises à Jour",
      noUpdates: "Aucune mise à jour disponible pour le moment.",
      comingSoon: "Bientôt disponible",
      scheduleMystery: "Planifier les Appels Mystère",
      scheduleMysterydesc: "Réserve tes futurs appels mystère directement depuis la plateforme",
      mysteryAssessment: "Évaluation des Appels Mystère",
      mysteryAssessmentDesc: "Complète les auto-évaluations et consulte les retours de ton tuteur",
      selfPacedCourses: "Cours Autonomes",
      selfPacedCoursesDesc: "Complète les cours en ligne et reçois des retours personnalisés de ton tuteur",
      progressTracking: "Suivi de Progression",
      progressTrackingDesc: "Surveille ton parcours d'apprentissage avec des informations détaillées sur ta progression",
    },
    common: {
      comingSoon: "Bientôt disponible",
      letsGo: "C'est parti",
      letsDiveIn: "On y va",
      loading: "Chargement...",
      back: "Retour",
      open: "Ouvrir",
    },
    dashboardExt: {
      monthlyMission: "Mission du mois",
      taglineCoaching: "Ton terrain d'entraînement privé",
      taglineMystery: "Sors de la salle de classe",
      taglineMission: "Chaque mois, on monte le niveau",
      coachingHook1: "Travail en profondeur. Déclics clairs. Vrais progrès.",
      coachingHook2: "C'est ici que l'on affine ta langue, qu'on aiguise ta réflexion et qu'on construit la confiance qui dure.",
      mysteryHook1: "Nouvel·le invité·e. Nouveau scénario. Aucune prévisibilité.",
      mysteryHook2: "Parle. Adapte-toi. Survis. Progresse.",
      missionHook1: "Pas de script. Pas de filet. Juste de la vraie conversation.",
      missionHook2: "C'est ici que la confiance se construit, pour de vrai.",
      thisMonthsMission: "La mission du mois",
      thisMonthsMissionDesc: "Ton défi mensuel pour faire passer ton français au niveau supérieur.",
      accessDocuments: "Documents",
      watchRecordings: "Enregistrements",
      viewFeedback: "Retours",
      workspaceNotSetUp: "Ton espace de travail n'a pas encore été configuré.",
      coachAlt: "Ta coach",
    },
    mysteryCallsPage: {
      pageTitle: "Appels Mystère",
      pageSubtitle: "Introduction",
      stepBeforeTitle: "Avant l'appel mystère",
      stepBeforeDesc: "Prépare-toi à réussir",
      stepDuringTitle: "Pendant l'appel mystère",
      stepDuringDesc: "Que faire pendant l'appel",
      stepAfterTitle: "Après l'appel mystère",
      stepAfterDesc: "Réfléchis et suis ta progression",
      stepGuideTitle: "Guide rapide",
      stepGuideDesc: "Un document de référence pratique",
      guideHeading: "Guide rapide",
      guideBlurb: "Un document de référence qui résume tout ce qu'il faut savoir sur les appels mystère.",
      openGuide: "Ouvrir le guide rapide",
      coursePlayerDesc: "Découvre les appels mystère !",
      schedulePageTitle: "Planification des Appels Mystère",
      schedulePageDesc: "Choisis tes créneaux disponibles pour les prochains appels mystère",
      scheduleAutoOpen: "Le calendrier devrait s'ouvrir automatiquement. Sinon, rafraîchis la page.",
    },
    mysteryStatusCalendar: {
      available: "Disponible",
      scheduled: "Planifié",
      toReschedule: "À reprogrammer",
      completed: "Terminé",
      cancelled: "Annulé",
      tooltipCancelled: "Annulé",
      tooltipToReschedule: "À reprogrammer – était à",
      tooltipCompletedAt: "Terminé à",
      tooltipScheduledAt: "Planifié à",
    },
    indexExt: {
      stayInformed: "Reste informé·e",
      whatsNext: "À venir",
    },
    resourcesExt: {
      taglineInduction: "Commence ici",
      taglineConfidence: "Sois toi-même, en français",
      taglinePhonetics: "Joue avec les sons",
      taglineVocabulary: "Des mots qui restent",
      taglineFluency: "Du haché au fluide",
      taglinePodcast: "Apprends en déplacement",
      taglineGrammar: "Des règles qui s'éclairent",
      taglineListening: "Entraîne ton oreille",
      // Card titles
      findYourFocusTitle: "Trouve ton focus",
      yourCoursePathwayTitle: "Ton parcours personnalisé",
      letsChatTitle: "On papote ?",
      blueprintTitle: "Le Blueprint",
      grammarPatternsTitle: "Schémas de grammaire",
      podcastFreebiesTitle: "Ressources du podcast",
      uhdListeningTitle: "Écoute HD",
      dictationPracticeTitle: "Dictées",
      speedListeningTitle: "Écoute rapide",
      // Card descriptions
      descSkillOfLearning: "Découvre comment apprendre les langues plus efficacement grâce à des stratégies éprouvées.",
      descFindYourFocus: "Identifie ta zone de focus et obtiens un parcours d'apprentissage personnalisé.",
      descFocusProfile: "Ton parcours personnalisé basé sur ta zone de focus.",
      descYourCoursePathway: "Obtiens un parcours sur mesure adapté à tes besoins. Trouve ton focus d'abord !",
      descConfidenceDiary: "Construis une confiance inébranlable grâce à des exercices de réflexion quotidiens.",
      descStayingConnected: "Reste connectée au français même dans tes journées les plus chargées.",
      descConfidencePlaylist: "Écoute des épisodes conçus pour booster ta confiance à l'oral.",
      descPronunciationRecorder: "Enregistre-toi et reçois des retours sur ta prononciation.",
      descAccentChallenge: "Transforme ton accent grâce à des défis quotidiens guidés.",
      descPhoneticsCorrection: "Affine les sons qui te posent le plus de difficultés.",
      descVocabStartHere: "Ton point de départ pour bâtir une base solide de vocabulaire.",
      descTopicsOfMonth: "Du vocabulaire thématique tout frais pour garder ton apprentissage dynamique.",
      descVocabPlaylist: "Élargis ton stock de mots avec du contenu audio sélectionné.",
      descPassiveToActive: "Transforme les mots que tu comprends en mots que tu utilises.",
      descLetsChat: "Un partenaire de conversation en français libre — pas de scénario, pas de script. Juste papoter.",
      descPathToFluency: "Commence ton voyage vers un français naturel et fluide.",
      descVerbEcho: "Développe tes réflexes verbaux grâce à des exercices d'écoute-réponse.",
      descBlueprint: "Construis des phrases françaises depuis la base avec des exercices guidés.",
      descConjugationTrainer: "Entraîne tes conjugaisons jusqu'à ce qu'elles deviennent une seconde nature.",
      descGrammarPatterns: "Décode les schémas de la grammaire française et fais-les tiens.",
      descPodcast: "Écoute des épisodes pensés pour booster ton français naturellement.",
      descPodcastFreebies: "Récupère des fiches gratuites et des ressources pour chaque épisode.",
      descUhdListening: "Affûte ton oreille avec des exercices d'écoute immersifs.",
      descDictationPractice: "Teste ta compréhension en écrivant ce que tu entends.",
      descSpeedListening: "Entraîne ton cerveau à traiter le français à vitesse naturelle.",
      // Pathway dialog
      pathwayDialogTitle: "Ton parcours personnalisé",
      pathwayDialogPara1: "C'est ici que tu obtiendras un parcours personnalisé conçu pour t'aider à choisir les meilleurs cours selon tes besoins.",
      pathwayDialogPara2: "Mais d'abord, identifions ta zone de focus — ça ne prend que quelques minutes.",
      pathwayDialogMaybeLater: "Plus tard",
      pathwayDialogFindMyFocus: "Trouver mon focus",
      // Focus profile titles
      focusSpeakingStructureTitle: "Renforcer les schémas et la structure",
      focusListeningTitle: "Affiner l'écoute et la compréhension",
      focusAnxietyTitle: "Bâtir la confiance en conversation",
      focusAccentTitle: "Affiner ton accent",
      // Misc toast
      notAvailableYet: "Pas encore disponible",
      noChatgptLink: "Ta coach ne t'a pas encore attribué de lien de pratique de conversation.",
    },
    billingExt: {
      title: "Facturation",
      loadingState: "Chargement...",
      manageDescription: "Gère ton abonnement et tes moyens de paiement.",
      activateDescription: "Active ton abonnement pour accéder à ton espace.",
      activateButton: "Activer mon abonnement",
      grandfatheredDesc: "Ton accès est actif. Enregistre ta carte pour être prélevé·e automatiquement le 1er de chaque mois.",
      unlimitedAccess: "Accès illimité",
      unlimitedAccessDesc: "Aucun changement requis pour le moment. Continue comme avant.",
      switchToAuto: "Passer au paiement automatique",
      switchToAutoDescPart1: "Enregistre ta carte aujourd'hui —",
      switchToAutoDescPart2: "aucun prélèvement immédiat",
      switchToAutoDescPart3: "Ton premier paiement automatique de",
      switchToAutoDescPart4: "aura lieu le",
      switchToAutoDescPart5: "puis le 1er de chaque mois.",
      saveMyCard: "Enregistrer ma carte",
      noChargeToday: "Aucun prélèvement aujourd'hui. Premier paiement le",
      accessSuspended: "Accès suspendu",
      accessSuspendedDesc: "Régularise ton paiement pour retrouver l'accès complet à la plateforme.",
      plan: "Formule",
      accessUntil: "Accès jusqu'au",
      nextRenewal: "Prochain renouvellement",
      manageSubscription: "Gérer mon abonnement",
      planNotAssigned: "Formule non attribuée",
      planNotAssignedDesc: "Contacte Nahéma pour qu'une formule te soit attribuée.",
      portalError: "Erreur",
      portalFailed: "Impossible d'ouvrir le portail",
      statusActive: "Actif",
      statusTrialing: "Période d'essai",
      statusPastDue: "Paiement en retard",
      statusCanceled: "Annulé",
      statusIncomplete: "Incomplet",
      statusUnpaid: "Impayé",
      statusPaused: "En pause",
      pausedTitle: "Paiement en pause",
      pausedDescWithDate: "Aucun prélèvement ne sera effectué à ta prochaine échéance. Ton accès reste entièrement ouvert et la facturation reprend automatiquement le",
      pausedDescNoDate: "Aucun prélèvement ne sera effectué pour le moment. Ton accès reste entièrement ouvert ; la facturation reprendra automatiquement plus tard.",
      billingResumes: "Reprise de la facturation le",
      cancelRequestButton: "Demander l'arrêt des cours",
      cancelRequestNote: "Un préavis d'un mois s'applique.",
      cancelDialogTitle: "Demander l'arrêt des cours",
      cancelDialogIntro: "Ton abonnement prévoit un préavis d'un mois. Voici exactement ce qui va se passer :",
      cancelBullet1: "Dernier prélèvement le",
      cancelBullet2: "Accès complet aux cours, aux ressources et aux mystery calls jusqu'au",
      cancelBullet3: "Après cette date, l'accès s'arrête et plus aucun prélèvement n'est effectué.",
      cancelBullet4: "Toi et Nahéma recevez immédiatement un email de confirmation.",
      cancelDialogBack: "Revenir en arrière",
      cancelDialogConfirm: "Confirmer ma demande",
      cancelScheduledTitle: "Arrêt programmé",
      cancelScheduledDesc: "Ton accès prend fin le",
      cancelScheduledContact: "Tu as changé d'avis ? Écris à Nahéma avant cette date.",
      cancelRequestDone: "Demande enregistrée",
      cancelRequestError: "Impossible d'enregistrer ta demande",
      tier245: "245 £ / mois",
      tier270: "270 £ / mois",
      tier300: "300 £ / mois",
    },
    notificationsExt: {
      comingSoonSuffix: " (bientôt disponible)",
    },
    profileSections: {
      password: {
        title: "Changer le mot de passe",
        description: "Mets à jour le mot de passe de ton compte",
        currentPassword: "Mot de passe actuel",
        newPassword: "Nouveau mot de passe",
        confirmPassword: "Confirmer le nouveau mot de passe",
        updateButton: "Mettre à jour le mot de passe",
        updating: "Mise à jour...",
        enterPasswords: "Veuillez remplir tous les champs de mot de passe",
        minLength: "Le mot de passe doit contenir au moins 6 caractères",
        mismatch: "Les nouveaux mots de passe ne correspondent pas",
        success: "Mot de passe mis à jour avec succès !",
        failed: "Échec de la mise à jour du mot de passe",
      },
      notifications: {
        title: "Préférences de notification",
        description: "Choisis les notifications que tu souhaites recevoir",
        mysteryCalls: "Rappels d'Appels Mystère",
        general: "Mises à jour et annonces générales",
        teacherFeedback: "Notifications de retours du professeur",
        updateFailed: "Échec de la mise à jour de la préférence",
      },
      legal: {
        title: "Documents légaux",
        description: "Consulte nos conditions et politiques",
        termsTitle: "Conditions générales",
        privacyTitle: "Politique de confidentialité",
        acceptance: "Acceptation des conditions",
        acceptanceText: "En accédant et en utilisant Le Coin Étude, tu acceptes d'être lié·e par ces Conditions Générales. Si tu n'es pas d'accord avec une partie de ces conditions, merci de ne pas utiliser nos services.",
        services: "Services",
        servicesText: "Le Coin Étude propose un coaching personnalisé en français, incluant des sessions 1-1, des Appels Mystère, des Real-World Missions et des ressources d'apprentissage autonomes. L'accès à certaines fonctionnalités peut nécessiter un abonnement actif.",
        userObligations: "Obligations de l'utilisateur",
        userObligationsText: "Tu t'engages à utiliser la plateforme de manière respectueuse et conforme à son usage prévu. Tu es responsable de la confidentialité de tes identifiants et de toutes les activités effectuées sous ton compte.",
        intellectualProperty: "Propriété intellectuelle",
        intellectualPropertyText: "Tout le contenu, les matériels et ressources fournis sur Le Coin Étude sont la propriété intellectuelle de Quirky French et ne peuvent être reproduits, distribués ou utilisés à des fins commerciales sans autorisation écrite préalable.",
        limitation: "Limitation de responsabilité",
        limitationText: "Le Coin Étude est fourni « tel quel ». Nous nous efforçons de fournir la meilleure expérience d'apprentissage mais ne pouvons garantir des résultats spécifiques. Nous ne sommes pas responsables des dommages indirects résultant de l'utilisation de nos services.",
        dataCollection: "Collecte des données",
        dataCollectionText: "Nous collectons les informations personnelles que tu fournis lors de l'inscription (nom, email, date de naissance) et les données d'utilisation telles que ta progression, tes enregistrements et l'historique de tes sessions pour personnaliser ton expérience.",
        dataUse: "Utilisation de tes données",
        dataUseText: "Tes données sont utilisées pour fournir et améliorer nos services, personnaliser ton expérience d'apprentissage, communiquer avec toi sur ta progression et envoyer des notifications pertinentes selon tes préférences.",
        dataProtection: "Protection des données",
        dataProtectionText: "Nous mettons en œuvre des mesures de sécurité conformes aux normes de l'industrie pour protéger tes données personnelles. Tes données sont stockées de manière sécurisée et ne sont jamais vendues à des tiers. Nous respectons les réglementations applicables en matière de protection des données.",
        yourRights: "Tes droits",
        yourRightsText: "Tu as le droit d'accéder, de corriger ou de supprimer tes données personnelles à tout moment. Tu peux mettre à jour tes préférences de notification dans les paramètres de ton profil. Pour demander la suppression de tes données, contacte-nous directement.",
      },
      billing: {
        title: "Facturation et paiements",
        description: "Gère tes moyens de paiement et consulte ton historique de facturation",
        comingSoon: "Bientôt disponible",
        paymentMethods: "Moyens de paiement",
        paymentMethodsDesc: "Ajoute et gère tes moyens de paiement",
        billingHistory: "Historique de facturation",
        billingHistoryDesc: "Consulte les factures et transactions passées",
        subscription: "Abonnement",
        subscriptionDesc: "Gère ton plan et abonnement actif",
      },
    },
    worksheet: {
      checklist: "Liste de contrôle",
      reflection: "Réflexion",
      personalNotes: "Notes personnelles",
      optional: "(facultatif)",
      notesPlaceholder: "Note tes réflexions supplémentaires…",
      thoughtsPlaceholder: "Écris tes réflexions ici…",
      saveProgress: "Sauvegarder",
      markComplete: "Marquer comme terminé",
      completed: "Terminé",
      moduleCompleted: "Étape terminée",
      progressSaved: "Progrès sauvegardé",
      progressSavedDesc: "Tes réponses ont été sauvegardées avec succès",
      moduleCompletedDesc: "Bravo ! Ton achèvement a été enregistré.",
      authRequired: "Authentification requise",
      authRequiredDesc: "Connecte-toi pour sauvegarder ta progression",
      errorSaving: "Erreur de sauvegarde",
      errorSavingDesc: "Impossible de sauvegarder ta progression",
    },
    voiceLab: {
      recordTitle: "Enregistre ta pratique",
      recordDesc: "Enregistre avec ton micro ou importe un fichier audio existant",
      clickToRecord: "Clique pour commencer l'enregistrement",
      or: "ou",
      uploadAudioFile: "Importer un fichier audio",
      uploadFormats: "MP3, WAV, M4A, WEBM ou OGG (max 20 Mo)",
      recordingInProgress: "Enregistrement en cours...",
      audioReady: "Audio prêt à être envoyé",
      previewSubmit: "Prévisualise et soumets ci-dessous",
      previewRecording: "Aperçu de l'enregistrement",
      reRecord: "Ré-enregistrer",
      recordingName: "Nom de l'enregistrement *",
      recordingNamePlaceholder: "ex. Pratique des sons nasaux, prononciation du R",
      notesOptional: "Notes (facultatif)",
      notesPlaceholder: "Ajoute des notes ou des questions pour ton coach...",
      uploading: "Envoi en cours...",
      uploadRecording: "Envoyer l'enregistrement",
      myRecordings: "Mes enregistrements",
      myRecordingsDesc: "Suis tes enregistrements soumis et consulte les retours",
      noRecordings: "Pas encore d'enregistrements. Commence à enregistrer pour recevoir des retours !",
      pending: "En attente",
      reviewed: "Relu",
      notes: "Notes",
      noNotes: "Pas de notes",
      yourFeedback: "Ton retour",
      waitingFeedback: "En attente de retour",
      addNotes: "Ajouter des notes...",
      playback: "Lecture de l'enregistrement",
      close: "Fermer",
      errorMic: "Erreur d'accès au microphone",
      invalidFile: "Type de fichier invalide",
      invalidFileDesc: "Importe un fichier audio (MP3, WAV, M4A, WEBM ou OGG)",
      fileTooLarge: "Fichier trop volumineux",
      fileTooLargeDesc: "Importe un fichier de moins de 20 Mo",
      fileUploaded: "Fichier importé",
      fileUploadedDesc: "Tu peux maintenant prévisualiser et soumettre ton enregistrement",
      missingInfo: "Informations manquantes",
      missingInfoDesc: "Enregistre ou importe un audio et fournis un nom d'enregistrement",
      recordingUploaded: "Enregistrement envoyé",
      recordingUploadedDesc: "Ton coach le révisera et te donnera un retour bientôt",
      errorUploading: "Erreur lors de l'envoi de l'enregistrement",
      errorLoading: "Erreur lors du chargement des enregistrements",
      errorLoadingRecording: "Erreur lors du chargement de l'enregistrement",
      errorDownloading: "Erreur lors du téléchargement de l'enregistrement",
      nameRequired: "Le nom est requis",
      recordingUpdated: "Enregistrement mis à jour",
      errorUpdating: "Erreur de mise à jour",
    },
    conversationPractice: {
      title: "On papote ?",
      description: "Parle de ce que tu veux, en français. Pas de scénario, pas de pression — juste une conversation.",
      myConversations: "Mes conversations",
      level: "Niveau",
      timerAria: "Temps écoulé",
      topicChip: "Sujet",
      topicLabel: "Sujet",
      topicComingSoonTitle: "Bientôt disponible",
      topicFree: "libre",
      topicPickerHeading: "Choisis un sujet",
      topicFreeOption: "Conversation libre (sans sujet)",
      topicCustomLabel: "Sujet personnalisé",
      topicCustomPlaceholder: "ex. mon weekend",
      topicComingSoonDesc: "C'est ici que tu pourras choisir le sujet du mois pour orienter ta pratique. À très bientôt !",
      preferences: "Réglages",
      preferencesAria: "Préférences",
      restart: "Recommencer",
      restartAria: "Recommencer",
      comeBackTomorrowTitle: "Reviens demain",
      comeBackTomorrowDesc: "Tu as déjà fait ta session du jour. Une nouvelle conversation sera disponible demain.",
      modeLabel: "Mode",
      modeFluency: "Fluidité",
      modeFluencyDesc: "Tour de parole naturel. Lola détecte tes pauses et répond toute seule.",
      modeComfort: "Confort",
      modeComfortDesc: "Réécoute-toi et refais avant d'envoyer à Lola.",
      modeHint: "Fluidité : tour de parole naturel — Lola détecte tes pauses et répond toute seule. Confort : réécoute-toi et refais avant d'envoyer.",
      pauseAutoHint: "Le temps de pause s'adapte automatiquement (~{seconds} s) — Lola soutenante attend plus, Lola exigeante coupe plus vite.",
      personalityLabel: "Personnalité de l'IA",
      personalitySupportive: "Soutenant",
      personalitySupportiveDesc: "Très patient. Vérifie, propose des mots, t'aide activement.",
      personalityNeutral: "Neutre",
      personalityNeutralDesc: "Comme un natif sympa. À toi de clarifier si besoin.",
      personalityJudgemental: "Exigeant",
      personalityJudgementalDesc: "Pressé, peu patient. Force-toi à être clair·e.",
      personalityHint: "Change comment l'IA réagit aux hésitations et aux phrases peu claires.",
      intensityLabel: "Intensité du caractère",
      intensityOff: "Off",
      intensitySubtle: "Discret",
      intensityBalanced: "Équilibré",
      intensityMarked: "Marqué",
      intensityAria: "Intensité du caractère de la voix",
      intensityHintJudgemental: "Plus tu montes, plus l'IA soupire et lâche des piques sarcastiques.",
      intensityHintSupportive: "Plus tu montes, plus l'IA est chaleureuse et rit doucement.",
      intensityHintNeutral: "Plus tu montes, plus l'IA marque ses pauses pensives.",
      vibeLabel: "Vibe",
      vibeAria: "L'énergie de Lola — de très soutenante à très exigeante",
      vibeAnchorSupportive: "Encourageante",
      vibeAnchorNeutral: "Neutre",
      vibeAnchorDemanding: "Exigeante",
      vibeHint: "Définit l'énergie de Lola — encourageante (chaleureuse, douce, rythme tranquille) à gauche, exigeante (plus stricte, plus rapide, te pousse à te dépasser) à droite.",
      transcriptLabel: "Affichage du texte",
      transcriptShown: "Transcription",
      transcriptHidden: "Texte masqué (mode oreille)",
      transcriptHint: "Masque le texte pour t'entraîner à comprendre uniquement à l'oreille.",
      levelA1: "A1 · Débutant",
      levelA2: "A2 · Élémentaire",
      levelB1: "B1 · Intermédiaire",
      levelB2: "B2 · Intermédiaire avancé",
      levelC1C2: "C1/C2 · Avancé",
      reviewHint: "Réécoute, recommence si besoin, puis envoie ton meilleur essai.",
      cancel: "Annuler",
      sendToAI: "Envoyer à l'IA",
      reviewTakeLabel: "Essai",
      reviewTakeLatest: "dernier",
      reviewTakePrevious: "précédent",
      reviewPlay: "Écouter",
      reviewPlaying: "Lecture…",
      reviewSlow: "Lent (0,75x)",
      reviewReRecord: "Recommencer",
      reviewSendThis: "Envoyer cet essai",
      reviewShowTranscript: "Voir la transcription",
      reviewHideTranscript: "Masquer la transcription",
      reviewTranscriptHint: "Ce qui a été compris (lecture seule). Si quelque chose cloche, recommence plutôt que de corriger.",
      replayLolaLine: "Réécouter Lola",
      needAHand: "Un coup de pouce ?",
      needAHandHint: "Quelques façons de commencer ta réponse — choisis-en une, puis continue avec tes mots.",
      startersLoading: "Je te trouve quelques amorces…",
      startersError: "Impossible de charger les amorces. Réessaie dans un instant.",
      startersHide: "Masquer les suggestions",
      ended: "Conversation terminée",
      endedDailyLimit: "Tu as atteint ta limite quotidienne de 6 minutes. Reviens demain pour une nouvelle conversation — en attendant, tu peux relire l'échange ou télécharger la transcription.",
      endedNormal: "Tu peux relire la conversation ci-dessus, télécharger la transcription, ou en commencer une nouvelle.",
      downloadTranscript: "Télécharger la transcription et le feedback",
      downloadAudio: "Télécharger l'audio (.wav)",
      audioDownloadEmptyTitle: "Aucun audio à télécharger",
      audioDownloadEmptyDesc: "L'audio n'est disponible qu'à la fin de la conversation. Rafraîchir la page le supprime.",
      audioDownloadFailedTitle: "Téléchargement audio impossible",
      audioDownloadFailedDesc: "Impossible d'assembler l'audio de la conversation.",
      comeBackTomorrowBtn: "Reviens demain",
      newConversation: "Nouvelle conversation",
      feedbackLoading: "Lola prépare ton feedback…",
      feedbackErrorTitle: "Feedback indisponible",
      feedbackErrorDesc: "Impossible de générer le feedback de Lola cette fois-ci. Tu peux quand même télécharger la transcription.",
      feedbackHeading: "Le débrief de Lola",
      feedbackPlayAgain: "Réécouter",
      feedbackPlaying: "Lecture…",
      feedbackOverall: "En résumé",
      feedbackReformulations: "Reformulations",
      feedbackReformulationsHint: "Tape sur une ligne pour entendre Lola la dire.",
      feedbackOriginalLabel: "Tu as dit",
      feedbackImprovedLabel: "Essaie plutôt",
      feedbackReachedFor: "Les mots que tu as cherchés",
      feedbackNextSteps: "La prochaine fois, essaie ça",
      feedbackFillerTitle: "Taux d'hésitations",
      feedbackFillerPer100: " /100 mots",
      feedbackFillerSuggested: "Essaie plutôt ces marqueurs français",
      feedbackDownloadNudge: "Garde tout — transcription + feedback — dans un PDF à relire :",
      startConversationAria: "Démarrer la conversation",
      hardCapHint: "Limite de 6 min atteinte — la conversation est terminée. Clique sur « Terminer » pour récupérer le transcript.",
      recordingFluency: "Je t'écoute… arrête-toi naturellement, je prendrai la parole.",
      recordingComfort: "Enregistrement… clique pour arrêter",
      transcribing: "Transcription en cours…",
      thinking: "L'IA réfléchit…",
      idleNotStarted: "Quand tu es prêt·e, clique pour démarrer la conversation.",
      idleSoftCap: "On approche de la fin — l'IA va clore la discussion. Tu peux échanger un dernier mot.",
      idleResumeFluency: "Clique pour reprendre la parole — ensuite ça enchaîne tout seul.",
      idleResumeComfort: "Clique pour parler",
      endConversation: "Terminer la conversation",
      wrapUpWithLola: "Conclure avec Lola",
      wrapUpHint: "Dès que tu cliques, Lola te fait son retour à l'oral — pose-toi et écoute.",
      wrappingUpTitle: "Lola fait le bilan…",
      wrappingUpDesc: "Elle va te partager son ressenti sur la conversation. Tends l'oreille.",
      softWarningTitle: "Bientôt l'heure de conclure",
      softWarningDesc: "Encore une trentaine de secondes — Lola va clore la conversation très vite.",
      softWarningCaption: "On y est presque — Lola va clore la conversation.",
      replay: "Réécouter",
      replaying: "Lecture…",
      replayMyVoiceAria: "Réécouter ma voix",
      audioPlaybackError: "Lecture audio impossible",
      audioPlaybackErrorDesc: "Impossible de générer l'audio.",
      insecureContextTitle: "Connexion non sécurisée",
      insecureContextDesc: "Le micro nécessite une connexion HTTPS. Recharge la page en https://.",
      browserNotSupportedTitle: "Navigateur non compatible",
      browserNotSupportedDesc: "Ton navigateur ne supporte pas l'enregistrement audio. Essaie Safari (iOS) ou Chrome (Android) à jour.",
      mediaRecorderUnsupportedDesc: "Ton navigateur ne supporte pas MediaRecorder. Mets-le à jour ou essaie un autre navigateur.",
      tooShortTitle: "Trop court",
      tooShortDesc: "Essaie de parler un peu plus longtemps.",
      nothingCapturedTitle: "Rien capté",
      nothingCapturedDesc: "On n'a pas réussi à transcrire — réessaie en parlant plus distinctement.",
      transcriptionError: "Erreur de transcription",
      genericErrorTitle: "Erreur",
      aiUnreachable: "Impossible de joindre l'IA.",
      dailyLimitTitle: "Limite quotidienne atteinte",
      dailyLimitDesc: "Tu as atteint les 6 minutes de conversation. Reviens demain pour une nouvelle session !",
      downloadFailedTitle: "Téléchargement impossible",
      downloadFailedDesc: "Impossible de générer le PDF.",
      micUnavailableTitle: "Micro indisponible",
      micUnavailableDesc: "Vérifie les permissions du navigateur.",
      micPermissionDeniedTitle: "Permission micro refusée",
      micPermissionDeniedIos: "Sur iPhone : ouvre Réglages iPhone → Safari → Microphone → autorise. Puis reviens ici, recharge la page (icône ↻ en bas) et réessaie. Si ça ne marche toujours pas, ferme complètement Safari et rouvre-le.",
      micPermissionDeniedDefault: "Touche l'icône cadenas 🔒 dans la barre d'adresse, autorise le micro, puis recharge la page.",
      micNoneTitle: "Aucun micro détecté",
      micNoneDesc: "Aucun micro n'est branché ou disponible sur cet appareil.",
      micBusyTitle: "Micro occupé",
      micBusyDesc: "Une autre app utilise déjà le micro (appel, WhatsApp, etc.). Ferme-la et réessaie.",
      micIncompatibleTitle: "Micro incompatible",
      micIncompatibleDesc: "Les contraintes audio ne sont pas supportées sur cet appareil.",
      micBlockedTitle: "Bloqué pour raisons de sécurité",
      micBlockedDesc: "Le navigateur bloque le micro. Vérifie que tu es bien sur une page sécurisée (https).",
      reloadPage: "Recharger la page",
      close: "Fermer",
    },
    errors: {
      notSupported: "Non supporté",
      browserNotSupported: "Ton navigateur ne supporte pas la reconnaissance vocale. Utilise Chrome ou Edge s'il te plaît.",
      error: "Erreur",
      errorLoading: "Erreur lors du chargement des ressources",
      errorLoadingAnalytics: "Échec du chargement des données d'analyse",
      recognitionError: "Erreur de reconnaissance",
      recognitionFailed: "Impossible de comprendre. Réessaie s'il te plaît.",
      voiceRecognitionError: "Impossible de démarrer la reconnaissance vocale.",
      responseFailed: "Échec de l'obtention de la réponse. Réessaie s'il te plaît.",
    },
    assessment: {
      // Page header
      pageTitle: "Trouve ton focus",
      pageDescription: "Identifie ta zone de focus et obtiens un parcours d'apprentissage personnalisé.",
      backToResources: "Retour aux ressources",
      // Steps meta
      stepIntroTitle: "Avant de commencer",
      stepIntroDesc: "Un guide rapide pour ton évaluation",
      stepReflectTitle: "Réfléchis",
      stepReflectDesc: "Partage ce que tu as en tête",
      stepRateTitle: "Évalue & réagis",
      stepRateDesc: "Évalue chaque affirmation honnêtement",
      stepResultsTitle: "Ton focus",
      stepResultsDesc: "Ton résultat personnalisé",
      // FocusIntroStep
      introHeading: "Trouve ton focus de fluidité",
      introMeta: "16 affirmations · environ 3 minutes",
      introLine1: "Ce petit test nous aide à identifier ton focus de fluidité actuel.",
      introLine2: "Il n'y a pas de bonne ou mauvaise réponse.",
      introLine3: "Réponds instinctivement.",
      introCta: "C'est parti",
      // Returning user view
      secondarySupport: "Soutien secondaire",
      viewMyResults: "Voir mes résultats",
      generating: "Génération…",
      openFocusPathway: "Ouvrir mon parcours focus",
      retakeTest: "Refaire le test",
      pdfError: "Impossible de générer le PDF.",
      // OpenEndedStep
      openEndedIntroHeading: "Prends un moment au calme",
      openEndedIntroLine1: "Les trois prochaines questions sont des réflexions ouvertes.",
      openEndedIntroLine2: "Écris simplement ce qui te vient — des réponses courtes sont tout à fait bien.",
      openEndedIntroLine3: "Nous utiliserons tes réponses pour comprendre ton profil d'apprentissage.",
      openEndedPlaceholder: "Écris librement…",
      openEndedQ1: "Quand tu penses à ton français en ce moment, qu'est-ce qui te semble frustrant, lourd ou limitant ?",
      openEndedQ2: "Si ces frustrations disparaissaient, comment te sentirais-tu en parlant français ?",
      openEndedQ3: "Qui deviendrais-tu en français si rien ne te retenait ?",
      analysing: "Analyse en cours…",
      analysisFailed: "L'analyse a échoué. On passe à l'étape suivante.",
      continue: "Continuer",
      // LikertCarousel
      likertNotTrue: "Pas vrai pour moi",
      likertSlightly: "Un peu vrai",
      likertSomewhat: "Plutôt vrai",
      likertMostly: "Vraiment vrai",
      likertVeryTrue: "Tout à fait vrai pour moi",
      answeredOf: "sur",
      answered: "complétées",
      previous: "Précédent",
      next: "Suivant",
      seeMyResult: "Voir mon résultat",
      // Likert questions
      likertQ1: "J'ai du mal à organiser mes pensées assez vite quand je parle.",
      likertQ2: "Mes phrases deviennent compliquées parce que j'essaie de paraître plus avancé·e que je ne le suis.",
      likertQ3: "Je commence souvent à parler avant de savoir comment finir ma phrase.",
      likertQ4: "Je connais le vocabulaire, mais je n'arrive pas toujours à le structurer clairement en temps réel.",
      likertQ5: "Je comprends bien mieux le français écrit que le français parlé.",
      likertQ6: "Quand quelqu'un parle vite, je perds le fil du message.",
      likertQ7: "J'ai besoin que les gens ralentissent ou se répètent fréquemment.",
      likertQ8: "Je me sens mentalement fatigué·e après avoir écouté du français rapide ou informel.",
      likertQ9: "Je ressens une tension physique (cœur qui bat, poitrine serrée, respiration courte) avant de parler.",
      likertQ10: "Je réfléchis trop à ce que je vais dire et je doute de moi.",
      likertQ11: "Je rejoue les conversations dans ma tête et je critique ma performance.",
      likertQ12: "J'évite les situations où je dois parler, même quand je sais que j'en suis capable.",
      likertQ13: "Je sens que ma prononciation affecte ma confiance.",
      likertQ14: "Les francophones ont parfois du mal à me comprendre clairement.",
      likertQ15: "Je trouve les sons ou le rythme du français difficiles à reproduire naturellement.",
      likertQ16: "J'ai l'impression que mon accent m'empêche de me sentir vraiment moi-même.",
      // FocusResultsStep
      analysingResponses: "Analyse de tes réponses",
      buildingProfile: "Création de ton profil personnalisé…",
      downloadPathway: "Télécharger le parcours PDF",
      startThisFocus: "Commencer ce focus",
      focusSet: "Ta zone de focus a été enregistrée !",
      saveError: "Une erreur s'est produite lors de la sauvegarde de ton résultat.",
      // Focus profile result titles & descriptions
      profileSpeakingResultTitle: "Ton focus principal : renforcer les schémas et la structure",
      profileSpeakingResultDesc: "Ce n'est pas le vocabulaire qui te manque. Tu as besoin de structure et de rapidité de décision.\nTon travail consistera à simplifier, organiser et construire des schémas de parole automatiques.",
      profileListeningResultTitle: "Ton focus principal : affiner l'écoute et la compréhension",
      profileListeningResultDesc: "Ton défi n'est ni ta capacité ni ton vocabulaire. C'est ta vitesse de traitement et ton endurance d'écoute.\nNous entraînerons ton cerveau à rester présent et à absorber un débit rapide sans panique.",
      profileAnxietyResultTitle: "Ton focus principal : bâtir la confiance en conversation",
      profileAnxietyResultDesc: "Ton plus grand obstacle n'est pas la langue — c'est la façon dont ton corps et ton esprit réagissent sous pression.\nNous travaillerons à rester calme, à te sentir toi-même en français et à bâtir une confiance conversationnelle durable.",
      profileAccentResultTitle: "Ton focus principal : affiner ton accent",
      profileAccentResultDesc: "Ta fluidité est plus solide que tu ne le penses.\nNous affinons maintenant la clarté, le rythme et le son pour gagner en présence et en confiance.",
    },
    vocabAssessment: {
      // Page header
      pageTitle: "À quel point es-tu doué·e pour apprendre du vocabulaire ?",
      pageDescription: "Une auto-évaluation rapide pour découvrir tes points forts et tes axes de progression en apprentissage du vocabulaire.",
      courseTitle: "Évaluation de vocabulaire",
      backToResources: "Retour aux ressources",
      // Steps
      stepIntroTitle: "Avant de commencer",
      stepIntroDesc: "Un guide rapide de ton évaluation",
      stepReflectTitle: "Réfléchis",
      stepReflectDesc: "Partage ce que tu as en tête",
      stepRateTitle: "Évalue & réagis",
      stepRateDesc: "Choisis ce qui te ressemble le plus",
      stepResultsTitle: "Ton niveau de vocabulaire",
      stepResultsDesc: "Ton résultat personnalisé",
      // Intro step
      introHeading: "À quel point es-tu doué·e pour apprendre du vocabulaire ?",
      introMeta: "11 questions + 3 réflexions · environ 4 minutes",
      introP1: "Salut, quirky learner ! Prêt·e à tester tes compétences en apprentissage du vocabulaire ? Que tu sois débutant·e ou explorateur·rice aguerri·e, ce test est pour toi. Et ne t'inquiète pas, ce n'est pas un quiz de vocabulaire.",
      introP2: "Dans ce test, je vais te poser des questions sur tes méthodes d'apprentissage, tes habitudes de révision et tes stratégies pour mémoriser de nouveaux mots. À la fin, je te proposerai des recommandations personnalisées pour t'accompagner dans ton parcours.",
      introP3: "Alors, prêt·e à découvrir tes forces et tes axes de progression ? C'est parti !",
      introCta: "C'est parti",
      // Open-ended
      reflectIntroHeading: "Prends un moment au calme",
      reflectIntroBody: "Les trois prochaines questions sont des réflexions ouvertes.\nÉcris simplement ce qui te vient — des réponses courtes sont parfaites.\nNous utiliserons tes réponses pour affiner ton score final.",
      reflectPlaceholder: "Écris librement…",
      reflectAnalysing: "Analyse en cours…",
      reflectAnalysisError: "L'analyse a échoué. Nous continuons sans le score IA.",
      open1: "Quand tu rencontres un nouveau mot dans ta langue cible, qu'en fais-tu habituellement ?",
      open2: "Raconte-moi une fois où un mot nouveau est vraiment resté gravé. Qu'est-ce qui t'a aidé·e à le retenir ?",
      open3: "Qu'est-ce qui te semble le plus difficile dans la construction de ton vocabulaire en ce moment ?",
      // Likert UI
      answeredOf: "{a} sur {t} répondu·es",
      previous: "Précédent",
      next: "Suivant",
      continue: "Continuer",
      seeMyResult: "Voir mon résultat",
      // Results
      analysingTitle: "Analyse de tes réponses",
      analysingBody: "Construction de ton résultat personnalisé…",
      levelLabel: "Niveau {n}",
      videoComingSoon: "Vidéo récap bientôt disponible",
      videoComingSoonHint: "Ta vidéo personnalisée apparaîtra ici.",
      viewMyResults: "Voir mes résultats",
      generating: "Génération…",
      downloadTips: "Télécharger mes conseils PDF",
      retakeTest: "Refaire le test",
      pdfError: "Impossible de générer le PDF.",
      saveError: "Nous n'avons pas pu sauvegarder tes résultats, mais tu peux toujours les consulter.",
      // Tier names
      tier1Name: "Le Novice du Vocabulaire",
      tier2Name: "L'Explorateur·rice du Vocabulaire",
      tier3Name: "L'Enthousiaste du Vocabulaire",
      tier4Name: "Le·La Maître·sse du Vocabulaire",
      // Questions and options (keyed by id)
      q1Question: "Comment apprends-tu généralement de nouveaux mots de vocabulaire ?",
      q1Opt0: "Je n'ai pas de méthode particulière",
      q1Opt1: "J'utilise des flashcards ou un logiciel de répétition espacée",
      q1Opt2: "J'écris des phrases avec les nouveaux mots en contexte",
      q1Opt3: "J'utilise activement les nouveaux mots à l'oral et à l'écrit",
      q1Opt4: "J'expérimente différentes méthodes pour voir ce qui me convient le mieux",
      q2Question: "À quelle fréquence révises-tu les mots de vocabulaire que tu as appris ?",
      q2Opt0: "Seulement quand j'ai un test ou un quiz à venir",
      q2Opt1: "Une fois par mois",
      q2Opt2: "Une fois par semaine",
      q2Opt3: "Tous les jours",
      q2Opt4: "J'utilise un logiciel de répétition espacée pour réviser régulièrement",
      q3Question: "Quelles stratégies utilises-tu pour mémoriser de nouveaux mots de vocabulaire ?",
      q3Opt0: "Je n'ai pas vraiment de stratégies",
      q3Opt1: "Je crée des moyens mnémotechniques pour m'aider à retenir les mots",
      q3Opt2: "J'utilise des supports visuels pour m'aider à retenir les mots",
      q3Opt3: "Je fais des liens avec des mots que je connais déjà",
      q3Opt4: "J'expérimente différentes méthodes jusqu'à trouver celle qui me convient le mieux",
      q4Question: "Comment intègres-tu les nouveaux mots dans ta pratique à l'oral et à l'écrit ?",
      q4Opt0: "Je n'intègre pas vraiment les nouveaux mots dans ma pratique",
      q4Opt1: "J'écris des phrases avec les nouveaux mots pour mieux les retenir",
      q4Opt2: "Je participe à des échanges linguistiques pour pratiquer les nouveaux mots à l'oral",
      q4Opt3: "Je parle avec des locuteurs natifs",
      q4Opt4: "J'intègre les mots dans ma routine quotidienne",
      q5Question: "À quel point es-tu à l'aise pour utiliser de nouveaux mots dans une conversation avec des natifs ?",
      q5Opt0: "Pas du tout à l'aise",
      q5Opt1: "Plutôt pas à l'aise",
      q5Opt2: "Ni à l'aise, ni mal à l'aise",
      q5Opt3: "Plutôt à l'aise",
      q5Opt4: "Très à l'aise",
      q6Question: "À quelle fréquence lis-tu dans la langue cible pour enrichir ton vocabulaire ?",
      q6Opt0: "Jamais",
      q6Opt1: "Rarement",
      q6Opt2: "De temps en temps",
      q6Opt3: "Quelques fois par semaine",
      q6Opt4: "Tous les jours ou presque",
      q7Question: "À quel point es-tu à l'aise pour chercher les mots que tu ne connais pas dans un dictionnaire ou en ligne ?",
      q7Opt0: "Pas du tout à l'aise",
      q7Opt1: "Plutôt à l'aise",
      q7Opt2: "Assez à l'aise",
      q7Opt3: "Très à l'aise",
      q7Opt4: "Extrêmement à l'aise",
      q8Question: "Comment détermines-tu la prononciation correcte des nouveaux mots ?",
      q8Opt0: "Je n'ai pas de méthode particulière",
      q8Opt1: "J'écoute des locuteurs natifs",
      q8Opt2: "J'utilise des guides de prononciation",
      q8Opt3: "Je m'entraîne à dire les mots à voix haute",
      q8Opt4: "J'expérimente différentes méthodes pour voir ce qui me convient le mieux",
      q9Question: "Comprends-tu la différence entre vocabulaire actif et vocabulaire passif ?",
      q9Opt0: "Non, je ne connais pas ces termes",
      q9Opt1: "J'en ai entendu parler, mais je ne suis pas sûr·e de ce que ça veut dire",
      q9Opt2: "Je sais ce que ça veut dire, mais j'ai du mal à activer mon vocabulaire passif",
      q9Opt3: "Je comprends la différence et je travaille activement à activer mon vocabulaire passif",
      q10Question: "Fais-tu un effort pour apprendre le vocabulaire en contexte ?",
      q10Opt0: "Non, je les mémorise simplement de façon isolée",
      q10Opt1: "J'essaie, mais c'est difficile",
      q10Opt2: "Oui, j'apprends toujours les nouveaux mots en contexte",
      q10Opt3: "Non seulement j'apprends les mots en contexte, mais j'essaie aussi de les utiliser dans mes propres phrases",
      q10Opt4: "Je cherche activement des supports authentiques pour apprendre du vocabulaire en contexte (livres, films, podcasts)",
      q11Question: "Comment évalues-tu tes progrès en vocabulaire ?",
      q11Opt0: "Je n'évalue pas vraiment mes progrès",
      q11Opt1: "J'essaie de noter les nouveaux mots que j'apprends, mais je ne me teste pas",
      q11Opt2: "Je me teste de temps en temps pour voir combien de mots je retiens",
      q11Opt3: "Je fais régulièrement des quiz de vocabulaire pour suivre mes progrès",
      q11Opt4: "J'ai un système détaillé pour suivre et catégoriser les nouveaux mots, et je révise fréquemment mes progrès",
    },
    myLessons: {
      back: "Tableau de bord",
      eyebrow: "1-1 avec Nahéma",
      title: "Mes leçons",
      intro: "Clique sur une leçon pour voir la vidéo, le résumé, la transcription et tes tâches — tout au même endroit.",
      loading: "Chargement…",
      empty: "Tes leçons apparaîtront ici dès que la prochaine synchronisation aura récupéré les enregistrements de ton dossier Drive.",
      videoBadge: "Vidéo",
      noVideo: "Pas de vidéo trouvée pour cette leçon.",
      fallbackTitle: "Leçon",
      tabTranscript: "Transcription",
      tabSummary: "Résumé",
      tabAskQuestion: "Poser une question",
      tabAskQuestionShort: "Question",
      tabTasks: "Tâches",
      summaryHeader: "Résumé",
      detailsHeader: "Détails",
      summaryUnavailable: "Résumé non disponible.",
      transcriptUnavailable: "Transcription non disponible.",
      translating: "Traduction en cours…",
      chatIntro: "Pose ta question sur cette leçon — l'IA a accès à la transcription complète.",
      chatPlaceholder: "Ta question…",
      chatThinking: "Réflexion en cours…",
      chatErrorPrefix: "Désolée, une erreur est survenue :",
      example1: "Quelles sont les principales corrections que tu m'as faites pendant cette leçon ?",
      example2: "Peux-tu m'expliquer à nouveau la règle grammaticale qu'on a abordée ?",
      example3: "Quels sont les 5 mots ou expressions les plus importants à retenir ?",
      example4: "À quoi est-ce que j'ai le plus progressé dans cette leçon ?",
      example5: "Quels points doivent faire l'objet de ma prochaine révision ?",
      tasksEmpty: "Pas encore de tâches. Ajoute ce que tu veux retravailler avant la prochaine leçon.",
      addTaskPlaceholder: "Ajouter une tâche…",
      deleteAria: "Supprimer",
    },
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === "fr" || stored === "en" ? stored : "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      // ignore storage errors
    }
  };

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && (e.newValue === "fr" || e.newValue === "en")) {
        setLanguageState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return typeof value === "string" ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
